import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTenantProfile, useTenantApiToken } from '../contexts/TenantProfileContext';
import {
  PackageOpen,
  Play,
  Pause,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  Loader2,
  Volume2,
  VolumeX,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CalendarPlus,
  BoxesIcon,
  Trash2,
  Sparkles,
  UtensilsCrossed,
  Bath,
  Home,
  Armchair,
  FileText,
  Zap,
  ExternalLink,
  DollarSign,
  Wifi,
  Users,
  Tag,
  Copy,
  Check,
  Upload,
  Paperclip,
  X,
  ImageIcon,
  Circle,
  ListChecks,
  Package,
  FileDown,
  MessageSquare,
  Send,
  Star,
  ChevronsDown,
  ChevronsUp,
  Lock,
  Clock,
} from 'lucide-react';
import { getMoveOutSlotsForDates, bookMoveOutSlot, SlotInfo, getMoveOutContent, MoveOutContentItem, getMoveOutLedger, LeaseLedgerData, LedgerTransaction, requestDisposalService, requestReferenceLetter, registerFurnitureSaleSellerConsent, getMoveOutChecklist, toggleChecklistItem, getChecklistUploadUrl, confirmChecklistAttachment, deleteChecklistAttachment, ChecklistItem, createFurnitureSaleItem, getFurnitureSaleItems, updateFurnitureSaleItem, deleteFurnitureSaleItem, FurnitureSaleItem, submitMoveOutFeedback, getDisposalWorkOrders, deleteDisposalWorkOrder, DisposalWorkOrder, uploadFurnitureProof, getFurnitureProof, deleteFurnitureProof, FurnitureProof } from '../services/tenantApi';
import { CHECKLIST_TEMPLATE, CLEANING_SUB_ITEMS } from '../config/checklist-template';
import { getApiOrigin } from '../config/api';
import confetti from 'canvas-confetti';
import heic2any from 'heic2any';

// ---------------------------------------------------------------------------
// Media URL resolver — makes server-relative paths absolute in production
// ---------------------------------------------------------------------------

function resolveMediaUrl(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${getApiOrigin()}${url}`;
}

// ---------------------------------------------------------------------------
// HEIC/HEIF detection & client-side conversion to JPEG
// ---------------------------------------------------------------------------

function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  const outputBlob = Array.isArray(blob) ? blob[0] : blob;
  const jpegName = file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg');
  return new File([outputBlob], jpegName, { type: 'image/jpeg' });
}

const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
];

function isAcceptedImage(file: File): boolean {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return true;
  const ext = file.name.toLowerCase();
  return ext.endsWith('.heic') || ext.endsWith('.heif');
}

// ---------------------------------------------------------------------------
// localStorage cache for move-out content so video URLs are available instantly
// on repeat visits (no waiting for Auth0 token or API round-trip).
// ---------------------------------------------------------------------------
const CONTENT_CACHE_KEY = 'moveout-content-cache';
const CONTENT_CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

function readCachedContent(): MoveOutContentItem[] | null {
  try {
    const raw = localStorage.getItem(CONTENT_CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw) as { ts: number; data: MoveOutContentItem[] };
    if (Date.now() - ts > CONTENT_CACHE_TTL_MS) {
      localStorage.removeItem(CONTENT_CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeCachedContent(data: MoveOutContentItem[]): void {
  try {
    localStorage.setItem(CONTENT_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota exceeded — ignore */ }
}

// ---------------------------------------------------------------------------
// Welcome video only: one video on the page. CloudFront URL from API (or cache).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Date helpers (Eastern Time, date-only -- no timezone conversion of dates)
// ---------------------------------------------------------------------------

function getEasternToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

const BOOKING_OPEN_DATE = new Date('2026-03-23T16:00:00Z'); // 12:00 PM EDT (UTC-4)

/** Call when self-service move-out booking is closed (72h before noon ET on lease end). */
const MOVE_OUT_BOOKING_PHONE_DISPLAY = '613-499-9860';
const MOVE_OUT_BOOKING_PHONE_TEL = '+16134999860';

function isBookingOpen(): boolean {
  return new Date() >= BOOKING_OPEN_DATE;
}

function getBookingCountdown(): { days: number; hours: number; minutes: number } | null {
  const now = new Date();
  const diff = BOOKING_OPEN_DATE.getTime() - now.getTime();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes };
}

function buildDateWindow(leaseEndDate: string, weekOffset: number = 0): string[] {
  const [y, m, d] = leaseEndDate.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  end.setDate(end.getDate() + weekOffset * 7);
  const dates: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const day = new Date(end);
    day.setDate(day.getDate() - i);
    const str = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    dates.push(str);
  }
  return dates;
}

function formatDayHeader(dateStr: string): { dayOfWeek: string; dateLabel: string } {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { dayOfWeek, dateLabel };
}

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

function formatSlotTime(isoUtc: string): string {
  const d = new Date(isoUtc);
  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatConfirmedDateISO(isoUtc: string): string {
  const d = new Date(isoUtc);
  const parts = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // en-CA gives YYYY-MM-DD
  return parts;
}

function formatConfirmedDayOfWeek(isoUtc: string): string {
  const d = new Date(isoUtc);
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long' });
}

function formatConfirmedTime(startIso: string, endIso: string): string {
  const start = formatSlotTime(startIso);
  const end = formatSlotTime(endIso);
  return `${start} – ${end} ET`;
}

// Grid rows 7 AM–9 PM ET (14 one-hour blocks). Booking picker hides some rows on the
// calendar day before lease end and on lease end day — see filterMoveOutSlotsForDisplay.
const CALENDAR_HOURS = Array.from({ length: 14 }, (_, i) => 7 + i);
const HOUR_HEIGHT = 52; // px per hour row
const HEADER_HEIGHT = 56; // px for the day header row
const TOTAL_GRID_HEIGHT = CALENDAR_HOURS.length * HOUR_HEIGHT;

/** Matches tenant-server `getSlotHours` day-before detection (calendar day before lease end). */
function computeDayBeforeLeaseDateStr(leaseEndDate: string): string {
  const endDate = new Date(`${leaseEndDate}T00:00:00`);
  const dayBefore = new Date(endDate);
  dayBefore.setDate(dayBefore.getDate() - 1);
  return dayBefore.toISOString().slice(0, 10);
}

function easternHourFromSlotStart(isoUtc: string): number {
  return parseInt(
    new Date(isoUtc).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }),
    10,
  );
}

/** Display-only: backend slot rules unchanged; existing bookings outside this window still show when confirmed. */
function filterMoveOutSlotsForDisplay(
  slotsByDate: Record<string, SlotInfo[]>,
  leaseEndDate: string | undefined,
): Record<string, SlotInfo[]> {
  if (!leaseEndDate) return slotsByDate;
  const dayBeforeStr = computeDayBeforeLeaseDateStr(leaseEndDate);
  const out: Record<string, SlotInfo[]> = {};
  for (const [dateStr, slots] of Object.entries(slotsByDate)) {
    out[dateStr] = slots.filter((s) => {
      const h = easternHourFromSlotStart(s.startTime);
      if (dateStr === leaseEndDate && h === 7) return false;
      if (dateStr === dayBeforeStr && h > 16) return false;
      return true;
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Section definitions (drives both the tracker and the page section IDs)
// ---------------------------------------------------------------------------

interface PageSection {
  id: string;
  label: string;
  step?: number;
}

const PAGE_SECTIONS: PageSection[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'book-appointment', label: 'Book Your Appointment', step: 1 },
  { id: 'expectations', label: 'Moving Out', step: 2 },
  { id: 'garbage-disposal', label: 'Disposal', step: 3 },
  { id: 'furniture-sale', label: 'Selling Furniture', step: 4 },
  { id: 'utilities', label: 'Closing Utilities', step: 5 },
  { id: 'internet', label: 'Internet', step: 6 },
  { id: 'reference-letter', label: 'Reference Letter', step: 7 },
  { id: 'settle-up', label: 'Final Balance', step: 8 },
  { id: 'feedback', label: 'Share Feedback', step: 9 },
  { id: 'google-review', label: 'Leave a Review', step: 10 },
];

// ---------------------------------------------------------------------------
// Section scroll tracker
// ---------------------------------------------------------------------------

function getScrollContainer(): HTMLElement | Window {
  return document.querySelector('main') ?? window;
}

function getScrollTop(): number {
  const container = getScrollContainer();
  return container instanceof Window ? container.scrollY : container.scrollTop;
}

function smoothScrollTo(top: number) {
  const container = getScrollContainer();
  container.scrollTo({ top, behavior: 'smooth' });
}

function SectionTracker({
  sections,
  activeId,
}: {
  sections: PageSection[];
  activeId: string | null;
}) {
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const container = getScrollContainer();
    const containerTop = container instanceof Window ? 0 : container.getBoundingClientRect().top;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')) || 0;
    const y = el.getBoundingClientRect().top - containerTop + getScrollTop() - headerHeight - 16;
    smoothScrollTo(y);
  };

  return (
    <nav className="hidden 2xl:flex flex-col items-center gap-0 fixed top-1/2 -translate-y-1/2 right-6 z-30">
      {sections.map((section, i) => {
        const isActive = section.id === activeId;
        const isLast = i === sections.length - 1;

        return (
          <div key={section.id} className="flex flex-col items-center">
            {/* Dot + label row */}
            <button
              onClick={() => handleClick(section.id)}
              className="flex items-center gap-2 group relative"
              title={section.label}
            >
              {/* Label (shows on hover or when active) */}
              <span
                className={`text-[11px] font-medium whitespace-nowrap transition-all duration-200 pr-1 ${
                  isActive
                    ? 'text-red-700 opacity-100'
                    : 'text-gray-500 opacity-100'
                }`}
              >
                {section.label}
              </span>

              {/* Dot or step number */}
              {section.step ? (
                <span
                  className={`rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-200 flex-shrink-0 ${
                    isActive
                      ? 'w-5 h-5 bg-red-700 text-white border-2 border-red-700 shadow-sm'
                      : 'w-5 h-5 bg-white text-gray-400 border-2 border-gray-300 group-hover:border-gray-400'
                  }`}
                >
                  {section.step}
                </span>
              ) : (
                <span
                  className={`rounded-full border-2 transition-all duration-200 flex-shrink-0 ${
                    isActive
                      ? 'w-3.5 h-3.5 bg-red-700 border-red-700 shadow-sm'
                      : 'w-2.5 h-2.5 bg-white border-gray-300 group-hover:border-gray-400'
                  }`}
                />
              )}
            </button>

            {/* Connector line */}
            {!isLast && (
              <div className="w-px h-6 bg-gray-200 my-1" />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Task summary (checklist overview with scroll-to-section)
// ---------------------------------------------------------------------------

const DUAL_TASK_ITEM_KEYS = new Set(['utilities-closed', 'internet-disconnected']);

const TASK_SECTION_MAP: Record<string, { sectionId: string; step: number }> = {
  'appointment-booked': { sectionId: 'book-appointment', step: 1 },
  'unit-cleaned': { sectionId: 'expectations', step: 2 },
  'disposal-plan-confirmed': { sectionId: 'garbage-disposal', step: 3 },
  'utilities-closed': { sectionId: 'utilities', step: 4 },
  'internet-disconnected': { sectionId: 'internet', step: 5 },
  'internet-router-returned': { sectionId: 'internet', step: 5 },
  'furniture-items-confirmed': { sectionId: 'furniture-sale', step: 7 },
  'furniture-proof-uploaded': { sectionId: 'furniture-sale', step: 7 },
  'furniture-sale-complete': { sectionId: 'furniture-sale', step: 7 },
};

function scrollToSection(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  const container = getScrollContainer();
  const containerTop = container instanceof Window ? 0 : container.getBoundingClientRect().top;
  const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')) || 0;
  const y = el.getBoundingClientRect().top - containerTop + getScrollTop() - headerH - 24;
  smoothScrollTo(y);
}

interface TaskSummaryItem {
  itemKey: string;
  label: string;
  sortOrder: number;
  completed: boolean;
}

function TaskSummary({ items, onNavigate }: { items: TaskSummaryItem[]; onNavigate?: (sectionId: string) => void }) {
  if (items.length === 0) return null;

  const completed = items.filter((i) => i.completed).length;
  const total = items.length;
  const allDone = completed === total;

  const handleClick = (sectionId: string) => {
    if (onNavigate) {
      onNavigate(sectionId);
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="px-5 pt-5 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ListChecks size={20} className="text-red-700 dark:text-red-400 shrink-0" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Summary of Tasks</h3>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            allDone
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {completed} / {total}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${allDone ? 'bg-green-600' : 'bg-red-600'}`}
            style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="px-3 pb-3">
        {items
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((item, idx) => {
            const taskNum = idx + 1;
            const mapping = TASK_SECTION_MAP[item.itemKey];
            return (
              <button
                key={item.itemKey}
                onClick={() => mapping && handleClick(mapping.sectionId)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left group"
              >
                {item.completed ? (
                  <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                ) : (
                  <Circle size={18} className="text-red-400 shrink-0 group-hover:text-red-500 transition-colors" />
                )}
                <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                  item.completed ? 'text-green-500' : 'text-gray-400'
                }`}>
                  Task {taskNum}
                </span>
                <span className={`text-sm flex-1 ${
                  item.completed ? 'text-gray-400 line-through' : 'text-gray-800'
                }`}>
                  {item.label}
                </span>
              </button>
            );
          })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const LANDFILL_ADDRESS = "1266 McAdoo's Ln, Glenburnie, ON K0H 1S0";

function DirectionsMap({ origin }: { origin: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [driveDuration, setDriveDuration] = useState<string | null>(null);
  const [driveDistance, setDriveDistance] = useState<string | null>(null);
  const [directionsOk, setDirectionsOk] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const loadedRef = useRef(false);

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(LANDFILL_ADDRESS)}&travelmode=driving`;

  useEffect(() => {
    if (loadedRef.current) return;
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey || !mapRef.current) return;
    loadedRef.current = true;

    const scriptId = 'google-maps-directions-script';
    const initMap = () => {
      const g = (window as any).google;
      if (!g?.maps || !mapRef.current) return;

      const LANDFILL_COORDS = { lat: 44.3075, lng: -76.4925 };

      const map = new g.maps.Map(mapRef.current, {
        zoom: 12,
        center: LANDFILL_COORDS,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
        styles: [
          { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 5 }] },
          { elementType: 'labels.text.fill', stylers: [{ saturation: -100 }, { lightness: -10 }] },
          { elementType: 'labels.text.stroke', stylers: [{ saturation: -100 }, { lightness: 100 }] },
          { featureType: 'water', elementType: 'geometry.fill', stylers: [{ saturation: -100 }, { lightness: 85 }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ saturation: -100 }, { lightness: 60 }] },
          { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ saturation: -100 }, { lightness: 40 }] },
          { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ saturation: -100 }, { lightness: 70 }] },
          { featureType: 'road.local', elementType: 'geometry.fill', stylers: [{ saturation: -100 }, { lightness: 80 }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'road.local', stylers: [{ visibility: 'simplified' }] },
          { featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
        ],
      });

      const directionsService = new g.maps.DirectionsService();
      const directionsRenderer = new g.maps.DirectionsRenderer({
        map,
        suppressMarkers: false,
        polylineOptions: { strokeColor: '#e22e07', strokeWeight: 4, strokeOpacity: 0.85 },
      });

      directionsService.route(
        {
          origin,
          destination: LANDFILL_ADDRESS,
          travelMode: g.maps.TravelMode.DRIVING,
        },
        (result: any, status: string) => {
          if (status === 'OK' && result) {
            directionsRenderer.setDirections(result);
            setDirectionsOk(true);
            const leg = result.routes?.[0]?.legs?.[0];
            if (leg) {
              setDriveDuration(leg.duration?.text ?? null);
              setDriveDistance(leg.distance?.text ?? null);
            }
          }
        },
      );
    };

    if ((window as any).google?.maps) {
      initMap();
    } else {
      const existing = document.getElementById(scriptId);
      if (!existing) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.async = true;
        script.defer = true;
        script.onload = initMap;
        script.onerror = () => setScriptError(true);
        document.head.appendChild(script);
      } else {
        existing.addEventListener('load', initMap);
      }
    }
  }, [origin]);

  if (scriptError) return null;

  return (
    <div className="space-y-2 pt-1">
      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Directions from Your Home</span>
      <div className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-white/10">
        <div ref={mapRef} style={{ width: '100%', height: 250 }} />

        {directionsOk && driveDuration && (
          <>
            <div className="absolute top-3 left-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 pointer-events-none">
              <span className="text-gray-900 dark:text-white text-sm font-bold">{driveDuration}</span>
              <span className="text-gray-400 dark:text-gray-500 mx-1.5">·</span>
              <span className="text-gray-600 dark:text-gray-400 text-sm">{driveDistance}</span>
            </div>
            <div className="absolute bottom-3 left-3 right-14 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-2.5 pointer-events-none flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">A</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Your Home</span>
              </div>
              <div className="flex-1 mx-3 flex items-center gap-2">
                <div className="flex-1 border-t-2 border-dashed border-gray-300 dark:border-white/15" />
                <div className="shrink-0 flex items-center gap-1.5 leading-none">
                  <span className="text-xs font-bold text-gray-900 dark:text-white leading-none">{driveDuration}</span>
                  <span className="text-gray-400 dark:text-gray-500 leading-none">·</span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-none">{driveDistance}</span>
                </div>
                <div className="flex-1 border-t-2 border-dashed border-gray-300 dark:border-white/15" />
              </div>
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">B</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Landfill</span>
              </div>
            </div>
          </>
        )}
      </div>
      {directionsOk && driveDuration ? (
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">{driveDuration}</span>
          <span className="text-gray-400 dark:text-gray-500">·</span>
          <span>{driveDistance}</span>
          <span className="text-gray-400 dark:text-gray-500">drive</span>
          <span className="text-gray-400 dark:text-gray-500">·</span>
          <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2">
            Open in Google Maps
          </a>
        </div>
      ) : (
        <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2">
          Get directions in Google Maps
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function VideoLoadingPlaceholder({ placeholderLabel }: { placeholderLabel: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="aspect-video bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={28} className="text-white/60 animate-spin mx-auto mb-2.5" />
          <p className="text-white/70 text-sm font-medium">{placeholderLabel}</p>
          <p className="text-white/40 text-xs mt-1">Loading video…</p>
        </div>
      </div>
    </div>
  );
}


function InlineVideo({ src, className }: { src: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onReady = () => setReady(true);
    const onWait = () => setReady(false);
    const onPlay = () => setReady(true);
    v.addEventListener('canplay', onReady);
    v.addEventListener('waiting', onWait);
    v.addEventListener('playing', onPlay);
    if (v.readyState >= 3) setReady(true);
    return () => {
      v.removeEventListener('canplay', onReady);
      v.removeEventListener('waiting', onWait);
      v.removeEventListener('playing', onPlay);
    };
  }, [src]);

  return (
    <div className="relative">
      <video
        ref={ref}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className={className}
      />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
          <Loader2 size={28} className="text-white animate-spin" />
        </div>
      )}
    </div>
  );
}

/**
 * Auto-play video player with controls. Renders directly when it has a CloudFront URL;
 * shows "Coming soon" placeholder otherwise. No queue — just render and let the browser fetch.
 */
function AutoPlayVideoPlayer({
  items,
  placeholderLabel,
  overlayTitle,
  aspectClass = 'aspect-video',
  bare = false,
  showSoundButton = true,
  darkControls = false,
}: {
  items: MoveOutContentItem[];
  placeholderLabel: string;
  overlayTitle?: string;
  aspectClass?: string;
  bare?: boolean;
  showSoundButton?: boolean;
  darkControls?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;
  const url = items[0]?.cloudfrontUrl;

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  }, []);

  const toggleSound = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  }, []);

  const changeSpeed = useCallback((rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setSpeed(rate);
    }
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !url) return;
    setVideoLoading(true);
    const onTime = () => {
      if (!v.duration || !isFinite(v.duration)) return;
      setProgress(v.currentTime / v.duration);
      const remaining = Math.ceil(v.duration - v.currentTime);
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      setTimeLeft(`${m}:${s.toString().padStart(2, '0')}`);
    };
    const onLoaded = () => {
      if (v.duration && isFinite(v.duration)) {
        setDuration(v.duration);
        const m = Math.floor(v.duration / 60);
        const s = Math.ceil(v.duration % 60);
        setTimeLeft(`${m}:${s.toString().padStart(2, '0')}`);
      }
    };
    const onCanPlay = () => setVideoLoading(false);
    const onWaiting = () => setVideoLoading(true);
    const onPlaying = () => setVideoLoading(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    if (v.readyState >= 3) setVideoLoading(false);
    if (v.duration && isFinite(v.duration)) onLoaded();
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
    };
  }, [url]);

  const RADIUS = 20;
  const STROKE = 3;
  const SIZE = (RADIUS + STROKE) * 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const offset = CIRCUMFERENCE - progress * CIRCUMFERENCE;

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
  }, []);

  if (items.length === 0 || !url) {
    if (bare) return null;
    return <VideoLoadingPlaceholder placeholderLabel={placeholderLabel} />;
  }

  const inner = (
    <div className="relative group">
      <video
        ref={videoRef}
        src={url}
        autoPlay
        muted
        loop
        playsInline
        className={`w-full ${aspectClass} bg-black`}
        preload="auto"
      />
      {videoLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
          <Loader2 size={32} className="text-white animate-spin" />
        </div>
      )}
      {overlayTitle && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 opacity-100 group-hover:opacity-0">
          <h3 className="text-white text-lg sm:text-xl font-bold tracking-wide drop-shadow-lg text-center px-8 py-3 bg-black/50 rounded-lg">
            {overlayTitle}
          </h3>
        </div>
      )}
      {duration > 0 && (
        <div
          className={`absolute bottom-0 left-0 right-0 h-1.5 cursor-pointer transition-all hover:h-2.5 ${darkControls ? 'bg-gray-300' : 'bg-white/20'}`}
          onClick={handleSeek}
        >
          <div
            className="h-full bg-red-600 rounded-r-full"
            style={{ width: `${progress * 100}%`, transition: 'width 0.25s linear' }}
          />
        </div>
      )}
      <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
        <button
          onClick={togglePlay}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg ${
            darkControls ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-900 hover:bg-gray-100'
          }`}
        >
          {playing ? <><Pause size={14} /><span>Pause</span></> : <><Play size={14} /><span>Play</span></>}
        </button>
        {showSoundButton && (
          <button
            onClick={toggleSound}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg ${
              muted
                ? darkControls ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-900 hover:bg-gray-100'
                : 'bg-black/60 text-white hover:bg-black/80'
            }`}
          >
            {muted ? (
              <>
                <VolumeX size={14} />
                <span>Play with sound</span>
              </>
            ) : (
              <>
                <Volume2 size={14} />
                <span>Turn sound off</span>
              </>
            )}
          </button>
        )}
        <button
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.currentTime = 0;
              videoRef.current.play();
              setPlaying(true);
            }
          }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg ${
            darkControls ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-900 hover:bg-gray-100'
          }`}
        >
          <RefreshCw size={14} />
          <span>Restart video</span>
        </button>
        <div className="relative">
          <button
            onClick={() => setSpeedOpen((o) => !o)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg ${
              darkControls ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{speed === 1 ? '1x' : `${speed}x`} Speed</span>
            <ChevronDown size={14} className={`transition-transform ${speedOpen ? 'rotate-180' : ''}`} />
          </button>
          {speedOpen && (
            <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-white/10 overflow-hidden min-w-[100px] z-10">
              {SPEEDS.map((rate) => (
                <button
                  key={rate}
                  onClick={() => {
                    changeSpeed(rate);
                    setSpeedOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors ${
                    speed === rate ? 'bg-red-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {rate === 1 ? '1x (Normal)' : `${rate}x`}
                </button>
              ))}
            </div>
          )}
        </div>
        {duration > 0 && (
          <div className="pointer-events-none">
            <div className="relative flex items-center justify-center" style={{ width: SIZE, height: SIZE }}>
              <svg width={SIZE} height={SIZE} className="absolute inset-0 -rotate-90">
                <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" stroke="rgba(226,46,7,0.25)" strokeWidth={STROKE} />
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke="#e22e07"
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={offset}
                  style={{ transition: 'stroke-dashoffset 0.25s linear' }}
                />
              </svg>
              <span className={`text-[10px] font-semibold tabular-nums drop-shadow-md ${darkControls ? 'text-gray-800' : 'text-white'}`}>{timeLeft}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (bare) return inner;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
      {inner}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section divider (numbered circle + title + horizontal rule)
// ---------------------------------------------------------------------------

function useHeaderHeight() {
  useEffect(() => {
    const header = document.querySelector('[data-sticky-header]');
    if (!header) return;

    const update = () => {
      document.documentElement.style.setProperty(
        '--sticky-header-h',
        `${header.getBoundingClientRect().height}px`
      );
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);
}

function CollapsibleSection({
  number,
  title,
  isComplete,
  defaultOpen,
  forceToggle,
  sectionId,
  forceOpenId,
  children,
}: {
  number: number;
  title: string;
  isComplete: boolean;
  defaultOpen?: boolean;
  forceToggle?: { open: boolean; rev: number };
  sectionId?: string;
  forceOpenId?: { sectionId: string; rev: number };
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? !isComplete);

  const prevRev = useRef(forceToggle?.rev ?? 0);
  useEffect(() => {
    if (forceToggle && forceToggle.rev !== prevRev.current) {
      prevRev.current = forceToggle.rev;
      setOpen(forceToggle.open);
    }
  }, [forceToggle?.rev, forceToggle?.open]);

  const prevOpenRev = useRef(forceOpenId?.rev ?? 0);
  useEffect(() => {
    if (forceOpenId && sectionId && forceOpenId.rev !== prevOpenRev.current) {
      prevOpenRev.current = forceOpenId.rev;
      if (forceOpenId.sectionId === sectionId) {
        setOpen(true);
      }
    }
  }, [forceOpenId?.rev, forceOpenId?.sectionId, sectionId]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-white/10">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`sticky z-10 w-full px-5 py-4 bg-white/95 backdrop-blur-sm text-left transition-colors rounded-t-2xl ${open ? 'border-b border-gray-100' : 'rounded-b-2xl'}`}
        style={{ top: 'var(--sticky-header-h, 0px)' }}
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold flex-shrink-0 transition-colors ${
            isComplete ? 'bg-green-600' : 'bg-red-700'
          }`}>
            {isComplete ? <Check size={14} /> : number}
          </div>
          <span className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
            isComplete ? 'text-green-700' : 'text-gray-500'
          }`}>{title}</span>
          {isComplete && <span className="text-[10px] font-medium text-green-600 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-full px-2 py-0.5">Complete</span>}
          <div className="flex-1" />
          <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`px-5 pt-5 pb-6 space-y-5 ${open ? '' : 'hidden'}`}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ Accordion
// ---------------------------------------------------------------------------

interface FaqItem {
  question: string;
  answer: string;
}

const MOVE_OUT_FAQ: FaqItem[] = [
  {
    question: 'What is a move-out appointment?',
    answer:
      'A move-out appointment is required prior to vacating your unit. Only one appointment is conducted per unit, and it should be attended by the tenant who will be vacating the unit last.\n\nDuring the appointment, we will conduct a move-out inspection which documents the condition of the unit being returned to us.\n\nYou will be vacating the space at the conclusion of the appointment, which means that the unit must be emptied of all personal items and garbage prior to the start of the appointment.',
  },
  {
    question: 'Why do I have to book one?',
    answer:
      'As outlined in your Lease Agreement, a move-out appointment is a mandatory part of the move-out process.\n\nIt allows our team to inspect the unit, document its condition, compare against the move-in inspection, collect keys, and identify any damages, cleaning, or garbage that may result in charges.\n\nWithout this appointment, we cannot formally process your departure or return of the unit.',
  },
  {
    question: 'How do I book one?',
    answer:
      'Use the calendar below to select a date within the final week of your lease, then pick an available time slot.\n\nAppointments are booked on a first-come, first-serve basis. Once all slots within a time window are taken, we cannot accommodate additional move-outs during that period — so book early!\n\nThe house manager is responsible for booking the appointment, but does not need to be in attendance if another tenant will be the last to leave.',
  },
  {
    question: 'What should I expect at this appointment?',
    answer:
      'You will be met at your unit by an Amber Peak representative at the time of your appointment.\n\nWe will walk through the property with you to thoroughly document the state of the unit. This includes noting any damages to compare against the move-in inspection, tenant possessions or garbage left behind, or cleaning required.\n\nIn the event that the state of the unit does not satisfy the obligation of being left in a "broom swept condition", you will receive an invoice for any notable damages, cleaning, or garbage collection.',
  },
  {
    question: 'What about key collection?',
    answer:
      'Where applicable, all keys for the unit — including mail keys — will be handed over at the time of the move-out appointment.\n\nPlease make sure you have all keys, fobs, garage remotes, and any other access devices gathered and ready to hand over when your appointment begins.',
  },
];

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="divide-y divide-gray-100">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i}>
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between py-3.5 text-left group"
            >
              <span className={`text-sm font-medium transition-colors ${isOpen ? 'text-red-700' : 'text-gray-800 group-hover:text-gray-900'}`}>
                {item.question}
              </span>
              <ChevronDown
                size={16}
                className={`flex-shrink-0 ml-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-red-700' : ''}`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-3.5' : 'max-h-0'}`}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed pr-6 whitespace-pre-line">{item.answer}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function toICSDateStringUTC(isoUtc: string): string {
  const d = new Date(isoUtc);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

function toICSDateStringET(isoUtc: string): string {
  const d = new Date(isoUtc);
  const pad = (n: number) => String(n).padStart(2, '0');
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}${get('month')}${get('day')}T${get('hour')}${get('minute')}${get('second')}`;
}

const ICS_VTIMEZONE = [
  'BEGIN:VTIMEZONE',
  'TZID:America/New_York',
  'BEGIN:STANDARD',
  'DTSTART:19701101T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
  'TZOFFSETFROM:-0400',
  'TZOFFSETTO:-0500',
  'TZNAME:EST',
  'END:STANDARD',
  'BEGIN:DAYLIGHT',
  'DTSTART:19700308T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
  'TZOFFSETFROM:-0500',
  'TZOFFSETTO:-0400',
  'TZNAME:EDT',
  'END:DAYLIGHT',
  'END:VTIMEZONE',
];

function buildPropertyLabel(lease: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number } | null | undefined): string {
  if (!lease?.propertyName) return '';
  if (lease.propertyUnitCount && lease.propertyUnitCount > 1 && lease.unitNumber) {
    return `#${lease.unitNumber} - ${lease.propertyName}`;
  }
  return lease.propertyName;
}

function escapeICS(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

function downloadICS(
  startTime: string,
  endTime: string,
  lease?: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number; address?: string | null } | null,
) {
  const now = new Date();
  const uid = `moveout-${now.getTime()}@amberpeak.ca`;
  const label = buildPropertyLabel(lease);
  const summary = label
    ? `Move-Out Appointment — ${label}`
    : 'Move-Out Appointment — Amber Peak';

  const descParts = [
    'WHAT IS INCLUDED',
    '• A walkthrough inspection of the unit with an Amber Peak representative',
    '• Documentation of the unit condition compared to the move-in inspection',
    '• Collection of all keys, fobs, garage remotes, and access devices',
    '• Identification of any damages, cleaning, or items left behind',
    '',
    'WHAT SHOULD I EXPECT',
    'You will be met at your unit by an Amber Peak representative at the time of your appointment. We will walk through the property with you to thoroughly document the state of the unit, including noting any damages, tenant possessions or garbage left behind, or cleaning required.',
    '',
    'If the unit does not satisfy the obligation of being left in a "broom swept condition", you will be given an opportunity to remedy the situation, or potentially be invoiced for additional damages, cleaning, or garbage collection, depending on the circumstances.',
    '',
    'BEFORE YOUR APPOINTMENT',
    '• The unit must be emptied of all personal items and garbage',
    '• Have all keys and access devices ready for collection',
    '• Ensure the unit is in broom-swept condition',
    '',
    'NEED TO RESCHEDULE?',
    'Visit the Amber Peak Move-Out Portal to reschedule your appointment:',
    'https://app.amberpeak.ca',
  ];

  if (lease?.address) {
    descParts.splice(0, 0, `LOCATION: ${lease.address}`, '');
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Amber Peak//Tenant Portal//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...ICS_VTIMEZONE,
    'BEGIN:VEVENT',
    `DTSTART;TZID=America/New_York:${toICSDateStringET(startTime)}`,
    `DTEND;TZID=America/New_York:${toICSDateStringET(endTime)}`,
    `DTSTAMP:${toICSDateStringUTC(now.toISOString())}`,
    `UID:${uid}`,
    `SUMMARY:${escapeICS(summary)}`,
    `DESCRIPTION:${escapeICS(descParts.join('\n'))}`,
    ...(lease?.address ? [`LOCATION:${escapeICS(lease.address)}`] : []),
    'STATUS:CONFIRMED',
    'URL:https://app.amberpeak.ca',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const apptDate = new Date(startTime).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const fileLabel = label ? label.replace(/[^a-zA-Z0-9 #-]/g, '').replace(/\s+/g, '-') : 'Amber-Peak';
  a.download = `Move-Out-Appointment-${fileLabel}-${apptDate}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ConfirmedCard({
  startTime,
  endTime,
  onReschedule,
  lease,
  selfServiceAvailable = true,
}: {
  startTime: string;
  endTime: string;
  onReschedule: () => void;
  lease?: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number; address?: string | null } | null;
  /** When false, Reschedule is hidden and phone CTA is shown (72h before lease-end noon ET). */
  selfServiceAvailable?: boolean;
}) {
  return (
    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-green-200 dark:border-green-900/40">
        <CheckCircle2 size={18} className="text-green-600 shrink-0" />
        <h3 className="text-sm font-semibold text-green-900">Your move-out is scheduled</h3>
      </div>

      {/* Details table */}
      <div className="divide-y divide-green-100">
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Date</span>
          <span className="text-sm text-green-900">{formatConfirmedDateISO(startTime)}</span>
        </div>
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Day</span>
          <span className="text-sm text-green-900">{formatConfirmedDayOfWeek(startTime)}</span>
        </div>
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Time</span>
          <span className="text-sm text-green-900">{formatConfirmedTime(startTime, endTime)}</span>
        </div>
      </div>

      {/* Footer */}
      <div className={`flex items-center px-5 py-3 border-t border-green-200 ${selfServiceAvailable ? 'justify-between' : 'justify-start'}`}>
        <button
          onClick={() => downloadICS(startTime, endTime, lease)}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors"
        >
          <CalendarPlus size={14} />
          Add to Calendar
        </button>
        {selfServiceAvailable ? (
          <button
            onClick={onReschedule}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors"
          >
            <RefreshCw size={14} />
            Reschedule
          </button>
        ) : null}
      </div>

      {!selfServiceAvailable ? (
        <div className="px-5 py-4 border-t border-amber-200 bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-950 space-y-2">
          <p>Online booking is now closed.</p>
          <p>
            If you would like to rebook your appointment, please call us at{' '}
            <a href={`tel:${MOVE_OUT_BOOKING_PHONE_TEL}`} className="font-semibold text-amber-900 underline decoration-amber-600/50 hover:decoration-amber-900">
              {MOVE_OUT_BOOKING_PHONE_DISPLAY}
            </a>
            .
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Shown when the portal-wide booking window is open but lease-end lockout prevents self-service (picker mode or no slots). */
function MoveOutSelfServiceUnavailablePanel() {
  return (
    <div className="text-center py-10 space-y-4">
      <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
        <Lock size={28} className="text-amber-600" />
      </div>
      <div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Online booking is now closed</h4>
        <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 max-w-md mx-auto leading-relaxed">
          If you would like to rebook your appointment, please call us at{' '}
          <a
            href={`tel:${MOVE_OUT_BOOKING_PHONE_TEL}`}
            className="font-semibold text-red-700 dark:text-red-400 hover:text-red-800 underline decoration-red-300"
          >
            {MOVE_OUT_BOOKING_PHONE_DISPLAY}
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function getSlotStyle(available: number, capacity: number): { bg: string; hoverBg: string; text: string; label: string } {
  if (available === 0) {
    return { bg: 'bg-red-100', hoverBg: '', text: 'text-red-800', label: 'Unavailable' };
  }
  const slotLabel = `${available} of ${capacity} slots available`;
  if (available === capacity) {
    return { bg: 'bg-green-50', hoverBg: 'hover:bg-green-100', text: 'text-green-700', label: slotLabel };
  }
  const pct = available / capacity;
  if (pct <= 0.25) {
    return { bg: 'bg-orange-100', hoverBg: 'hover:bg-orange-200', text: 'text-orange-800', label: slotLabel };
  }
  if (pct <= 0.5) {
    return { bg: 'bg-amber-50', hoverBg: 'hover:bg-amber-100', text: 'text-amber-800', label: slotLabel };
  }
  return { bg: 'bg-yellow-50', hoverBg: 'hover:bg-yellow-100', text: 'text-yellow-800', label: slotLabel };
}

function WeekCalendar({
  dates,
  slotsByDate,
  loading,
  todayET,
  booking,
  onBook,
  onPrevWeek,
  onNextWeek,
  canGoForward,
}: {
  dates: string[];
  slotsByDate: Record<string, SlotInfo[]>;
  loading: boolean;
  todayET: string;
  booking: boolean;
  onBook: (slot: SlotInfo) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  canGoForward: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const weekRangeLabel = dates.length > 0
    ? (() => {
        const first = formatDayHeader(dates[0]);
        const last = formatDayHeader(dates[dates.length - 1]);
        return `${first.dateLabel} – ${last.dateLabel}`;
      })()
    : '';

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={onPrevWeek} className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-gray-100">
            <ChevronLeft size={16} />
            <span>Earlier</span>
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{weekRangeLabel}</span>
          <button onClick={onNextWeek} disabled={!canGoForward} className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${canGoForward ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}>
            <span>Later</span>
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-red-700 dark:text-red-400" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading availability...</span>
        </div>
      </div>
    );
  }

  const findSlotForHour = (dateStr: string, hour: number): SlotInfo | undefined => {
    const daySlots = slotsByDate[dateStr] ?? [];
    return daySlots.find((s) => easternHourFromSlotStart(s.startTime) === hour);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={onPrevWeek} className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-gray-100">
          <ChevronLeft size={16} />
          <span>Earlier</span>
        </button>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{weekRangeLabel}</span>
        <button onClick={onNextWeek} disabled={!canGoForward} className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${canGoForward ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}>
          <span>Later</span>
          <ChevronRight size={16} />
        </button>
      </div>
    <div className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
      {/* Sticky header with day names */}
      <div
        className="grid bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10 sticky top-0 z-10"
        style={{ gridTemplateColumns: `48px repeat(${dates.length}, 1fr)` }}
      >
        <div className="h-14 border-r border-gray-200 dark:border-white/10" />
        {dates.map((d) => {
          const { dayOfWeek, dateLabel } = formatDayHeader(d);
          const isPast = d < todayET;
          const isToday = d === todayET;
          return (
            <div
              key={d}
              className={`flex flex-col items-center justify-center h-14 text-xs border-r border-gray-200 last:border-r-0 ${
                isPast ? 'text-gray-300' : isToday ? 'text-red-700 font-bold' : 'text-gray-700'
              }`}
            >
              <span className="font-semibold">{dayOfWeek}</span>
              <span className={isToday ? 'bg-red-700 text-white rounded-full px-1.5 text-[10px] leading-4' : ''}>{dateLabel}</span>
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: `${HOUR_HEIGHT * 10}px` }}>
        <div className="relative" style={{ height: `${TOTAL_GRID_HEIGHT}px` }}>
          {/* Hour rows */}
          {CALENDAR_HOURS.map((hour, i) => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-b border-gray-100 dark:border-white/5"
              style={{ top: `${i * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
            >
              {/* Time label */}
              <div className="absolute left-0 w-12 h-full flex items-center justify-end pr-1.5 border-r border-gray-200 dark:border-white/10">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">{formatHourLabel(hour)}</span>
              </div>
            </div>
          ))}

          {/* Day columns with slot cells */}
          <div
            className="absolute top-0 bottom-0 right-0"
            style={{ left: '48px', display: 'grid', gridTemplateColumns: `repeat(${dates.length}, 1fr)` }}
          >
            {dates.map((dateStr) => {
              const isPast = dateStr < todayET;
              return (
                <div key={dateStr} className="relative border-r border-gray-100 dark:border-white/5 last:border-r-0">
                  {CALENDAR_HOURS.map((hour, i) => {
                    const slot = findSlotForHour(dateStr, hour);
                    const full = slot ? slot.available === 0 : true;
                    const noSlot = !slot;
                    const unavailable = isPast || noSlot || full;

                    const avail = slot?.available ?? 0;
                    const { bg, hoverBg, text: textColor, label: slotLabel } = getSlotStyle(avail, slot?.capacity ?? 4);

                    return (
                      <button
                        key={hour}
                        disabled={unavailable || booking}
                        onClick={() => slot && onBook(slot)}
                        className={`absolute left-0 right-0 border-b border-gray-100 flex items-center justify-center transition-colors ${
                          isPast
                            ? 'bg-gray-50/60 cursor-not-allowed'
                            : noSlot
                            ? 'bg-gray-50/40 cursor-not-allowed'
                            : `${bg} ${unavailable ? 'cursor-not-allowed' : `${hoverBg} cursor-pointer`}`
                        }`}
                        style={{ top: `${i * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                        title={
                          slot && !isPast
                            ? `${formatHourLabel(hour)} – ${formatHourLabel(hour + 1)} ET · ${slot.available} of ${slot.capacity} available`
                            : isPast
                            ? 'Past date'
                            : ''
                        }
                      >
                        {!isPast && slot && (
                          <span className={`text-[10px] font-medium leading-tight text-center px-0.5 ${textColor}`}>
                            {slotLabel}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer legend */}
      <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1 py-2 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-[10px] text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40" /> Open</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-50 border border-yellow-200" /> Limited</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-200" /> Almost full</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-900/40" /> Unavailable</span>
        <span>All times Eastern</span>
      </div>
    </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline calendar date picker with highlighted dates
// ---------------------------------------------------------------------------

interface ServiceCalendarProps {
  selected: string;
  onSelect: (date: string) => void;
  minDate: string;
  maxDate: string;
  leaseEndDate: string | null;
  appointmentDate: string | null;
}

function ServiceCalendar({ selected, onSelect, minDate, maxDate, leaseEndDate, appointmentDate }: ServiceCalendarProps) {
  const leaseEndPrev = leaseEndDate
    ? (() => { const d = new Date(leaseEndDate + 'T12:00:00'); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })()
    : null;
  const apptDateStr = appointmentDate ? new Date(appointmentDate).toISOString().slice(0, 10) : null;

  const initialMonth = leaseEndDate
    ? { year: parseInt(leaseEndDate.slice(0, 4)), month: parseInt(leaseEndDate.slice(5, 7)) - 1 }
    : { year: new Date().getFullYear(), month: new Date().getMonth() };

  const [viewYear, setViewYear] = useState(initialMonth.year);
  const [viewMonth, setViewMonth] = useState(initialMonth.month);

  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };

  const pad = (n: number) => String(n).padStart(2, '0');

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="w-[280px]">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 transition-colors"><ChevronLeft size={16} className="text-gray-600 dark:text-gray-400" /></button>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 transition-colors"><ChevronRight size={16} className="text-gray-600 dark:text-gray-400" /></button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase py-1">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;

          const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
          const isDisabled = dateStr < minDate || dateStr > maxDate;
          const isSelected = dateStr === selected;
          const isLeaseEnd = dateStr === leaseEndDate;
          const isLeaseEndPrev = dateStr === leaseEndPrev;
          const isAppt = dateStr === apptDateStr;
          const isToday = dateStr === getEasternToday();

          let cellClass = 'text-gray-700 hover:bg-gray-100';
          let dotColor = '';

          if (isDisabled) {
            cellClass = 'text-gray-300 cursor-not-allowed';
          } else if (isSelected) {
            cellClass = 'bg-red-700 text-white font-bold';
          } else if (isLeaseEnd) {
            cellClass = 'bg-red-100 text-red-800 font-semibold hover:bg-red-200';
            dotColor = 'bg-red-500';
          } else if (isAppt) {
            cellClass = 'bg-green-100 text-green-800 font-semibold hover:bg-green-200';
            dotColor = 'bg-green-500';
          } else if (isLeaseEndPrev) {
            cellClass = 'bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200';
            dotColor = 'bg-gray-500';
          }

          return (
            <button
              key={dateStr}
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelect(dateStr)}
              className={`relative flex flex-col items-center justify-center h-8 rounded-md text-xs transition-colors ${cellClass}`}
            >
              {isToday && !isSelected && <div className="absolute top-0.5 w-1 h-1 rounded-full bg-gray-400" />}
              {day}
              {dotColor && !isSelected && <div className={`absolute bottom-0.5 w-1 h-1 rounded-full ${dotColor}`} />}
            </button>
          );
        })}
      </div>

      {/* Legend — only show items whose dates fall within the selectable range */}
      {(() => {
        const inRange = (d: string | null) => d != null && d >= minDate && d <= maxDate;
        const showLeaseEnd = inRange(leaseEndDate);
        const showLeaseEndPrev = inRange(leaseEndPrev);
        const showAppt = inRange(apptDateStr);
        if (!showLeaseEnd && !showLeaseEndPrev && !showAppt) return null;
        return (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 pt-2 border-t border-gray-100 dark:border-white/5">
            {showLeaseEnd && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Last day of lease
              </span>
            )}
            {showLeaseEndPrev && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                <span className="w-2 h-2 rounded-full bg-gray-500" /> Day before
              </span>
            )}
            {showAppt && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                <span className="w-2 h-2 rounded-full bg-green-500" /> Your Move Out Appointment
              </span>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function MoveOutPage() {
  const { tenant, loading: profileLoading, refreshProfile } = useTenantProfile();
  const getToken = useTenantApiToken();
  useHeaderHeight();

  const moveOut = tenant?.moveOut ?? null;

  const [sectionToggle, setSectionToggle] = useState<{ open: boolean; rev: number }>({ open: false, rev: 0 });
  const [forceOpenId, setForceOpenId] = useState<{ sectionId: string; rev: number }>({ sectionId: '', rev: 0 });
  const expandAll = useCallback(() => setSectionToggle((s) => ({ open: true, rev: s.rev + 1 })), []);
  const collapseAll = useCallback(() => setSectionToggle((s) => ({ open: false, rev: s.rev + 1 })), []);

  const [mode, setMode] = useState<'confirmed' | 'picker'>('picker');
  const [slotsByDate, setSlotsByDate] = useState<Record<string, SlotInfo[]>>({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  const [bookingOpen, setBookingOpen] = useState(isBookingOpen);
  const [countdown, setCountdown] = useState(getBookingCountdown);

  useEffect(() => {
    if (bookingOpen) return;
    const timer = setInterval(() => {
      if (isBookingOpen()) {
        setBookingOpen(true);
        setCountdown(null);
        clearInterval(timer);
      } else {
        setCountdown(getBookingCountdown());
      }
    }, 30_000);
    return () => clearInterval(timer);
  }, [bookingOpen]);
  const [pageContent, setPageContent] = useState<MoveOutContentItem[]>(() => readCachedContent() ?? []);
  const [contentLoadError, setContentLoadError] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(() => readCachedContent() === null);
  const [ledger, setLedger] = useState<LeaseLedgerData | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [serviceRequesting, setServiceRequesting] = useState<string | null>(null);
  const [serviceRequested, setServiceRequested] = useState<Set<string>>(new Set());
  const [serviceWorkOrderRefs, setServiceWorkOrderRefs] = useState<Record<string, number>>({});
  const [servicePickingDate, setServicePickingDate] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState<string>('');
  const [disposalWorkOrders, setDisposalWorkOrders] = useState<DisposalWorkOrder[]>([]);
  const [disposalDeleteConfirm, setDisposalDeleteConfirm] = useState<string | null>(null);
  const [disposalDeleting, setDisposalDeleting] = useState<string | null>(null);
  const [furnitureSaleSellerConsent, setFurnitureSaleSellerConsent] = useState(false);
  const [furnitureSaleSubmitting, setFurnitureSaleSubmitting] = useState(false);
  const [furnitureSaleSubmitted, setFurnitureSaleSubmitted] = useState(false);
  const [furnitureSaleConsentResult, setFurnitureSaleConsentResult] = useState<{
    actions?: Array<{ step: string; status: 'completed' | 'skipped' | 'failed'; detail: string }>;
  } | null>(null);
  const [referenceLetterRequesting, setReferenceLetterRequesting] = useState(false);
  const [referenceLetterRequested, setReferenceLetterRequested] = useState(false);
  const [foodBankOpen, setFoodBankOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Furniture sale items state
  const [saleItems, setSaleItems] = useState<FurnitureSaleItem[]>([]);
  const [saleItemsLoading, setSaleItemsLoading] = useState(true);
  // Furniture sale proof-of-purchase state (initialized from /me response, no separate fetch)
  const [furnitureProof, setFurnitureProof] = useState<FurnitureProof | null>(null);
  const [furnitureProofUploading, setFurnitureProofUploading] = useState(false);
  const [furnitureProofDeleting, setFurnitureProofDeleting] = useState(false);
  const furnitureProofInputRef = useRef<HTMLInputElement>(null);
  // Multi-photo drag-and-drop tagging wizard state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [taggingIndex, setTaggingIndex] = useState(0);
  const [taggingName, setTaggingName] = useState('');
  const [taggingDescription, setTaggingDescription] = useState('');
  const [taggingUploading, setTaggingUploading] = useState(false);
  const [heicConverting, setHeicConverting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dropInputRef = useRef<HTMLInputElement | null>(null);

  const taggingPreviewUrl = useMemo(() => {
    const file = pendingFiles[taggingIndex];
    if (!file) return null;
    const url = URL.createObjectURL(file);
    return url;
  }, [pendingFiles, taggingIndex]);

  useEffect(() => {
    return () => {
      if (taggingPreviewUrl) URL.revokeObjectURL(taggingPreviewUrl);
    };
  }, [taggingPreviewUrl]);

  // Checklist state — DB records represent completed/in-progress items only
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [checklistMoveOutId, setChecklistMoveOutId] = useState<string | null>(null);
  const [checklistLoading, setChecklistLoading] = useState(true);

  // Derived task list: template is source of truth, DB records indicate completion
  const mergedTasks = useMemo(() =>
    CHECKLIST_TEMPLATE.map((t) => {
      const dbItem = checklistItems.find((ci) => ci.itemKey === t.itemKey);
      return {
        itemKey: t.itemKey,
        type: t.type,
        label: t.label,
        category: t.category,
        sortOrder: t.sortOrder,
        completed: dbItem?.completed ?? false,
        completedAt: dbItem?.completedAt ?? null,
        attachment: dbItem?.attachment ?? null,
        dbId: dbItem?.id ?? null,
        metadata: dbItem?.metadata ?? null,
      };
    }),
    [checklistItems]
  );
  type MergedTask = typeof mergedTasks[number];

  const mergedCleaningSubs = useMemo(() =>
    CLEANING_SUB_ITEMS.map((s) => {
      const dbItem = checklistItems.find((ci) => ci.itemKey === s.itemKey);
      return {
        itemKey: s.itemKey,
        label: s.label,
        category: s.category,
        tier: s.tier,
        sortOrder: s.sortOrder,
        completed: dbItem?.completed ?? false,
        dbId: dbItem?.id ?? null,
      };
    }),
    [checklistItems]
  );
  const requiredSubs = mergedCleaningSubs.filter((s) => s.tier === 'required');
  const allCleaningSubsDone = requiredSubs.every((s) => s.completed);
  const cleaningSubsDone = requiredSubs.filter((s) => s.completed).length;
  const cleaningSubsTotal = requiredSubs.length;

  const [requiredCopied, setRequiredCopied] = useState(false);

  const buildChecklistText = (items: typeof mergedCleaningSubs, title: string) => {
    const categories = ['Kitchen', 'Bathroom', 'Unit'] as const;
    const lines: string[] = [`${title}`, ''];
    for (const cat of categories) {
      const catItems = items.filter((s) => s.category === cat);
      if (catItems.length === 0) continue;
      const heading = cat === 'Unit' ? 'The Unit' : cat === 'Bathroom' ? 'Bathrooms' : cat;
      const done = catItems.filter((s) => s.completed).length;
      lines.push(`${heading} (${done}/${catItems.length})`);
      for (const item of catItems) {
        lines.push(`  ${item.completed ? '\u2705' : '\u2B1C'} ${item.label}`);
      }
      lines.push('');
    }
    const totalDone = items.filter((s) => s.completed).length;
    lines.push(`Progress: ${totalDone}/${items.length}`);
    return lines.join('\n');
  };

  const handleCopyChecklist = () => {
    const text = buildChecklistText(requiredSubs, 'Move-Out Cleaning Checklist');
    navigator.clipboard.writeText(text).then(() => {
      setRequiredCopied(true); setTimeout(() => setRequiredCopied(false), 2000);
    });
  };

  const handleExportChecklistPdf = () => {
    const categories = ['Kitchen', 'Bathroom', 'Unit'] as const;
    const lease = tenant?.residentialLease;
    const unitLine = [lease?.unitNumber, lease?.propertyName].filter(Boolean).join(' — ') || '';
    const generatedDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });

    const buildSection = (items: typeof mergedCleaningSubs, title: string, accentColor: string, bgColor: string, checkColor: string) => {
      const done = items.filter((s) => s.completed).length;
      let html = `<div style="margin-bottom:28px">
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 14px;background:${bgColor};border-radius:6px;margin-bottom:12px">
          <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:${accentColor}">${title}</span>
          <span style="font-size:11px;font-weight:600;color:${done === items.length ? '#15803d' : '#6b7280'}">${done}/${items.length} complete</span>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px">`;

      for (const cat of categories) {
        const catItems = items.filter((s) => s.category === cat);
        if (catItems.length === 0) continue;
        const heading = cat === 'Unit' ? 'The Unit' : cat === 'Bathroom' ? 'Bathrooms' : cat;
        const catDone = catItems.filter((s) => s.completed).length;
        html += `<div>
          <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid #e5e7eb;margin-bottom:8px">
            <span style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:#374151">${heading}</span>
            <span style="font-size:10px;font-weight:600;color:${catDone === catItems.length ? '#15803d' : '#9ca3af'}">${catDone}/${catItems.length}</span>
          </div>`;
        for (const item of catItems) {
          const box = item.completed
            ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:3px;background:${checkColor};color:white;font-size:10px;flex-shrink:0">&#10003;</span>`
            : `<span style="display:inline-block;width:14px;height:14px;border-radius:3px;border:1.5px solid #d1d5db;flex-shrink:0"></span>`;
          const textStyle = item.completed ? 'color:#9ca3af;text-decoration:line-through' : 'color:#374151';
          html += `<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:5px">
            ${box}
            <span style="font-size:11px;line-height:1.4;${textStyle}">${item.label}</span>
          </div>`;
        }
        html += `</div>`;
      }
      html += `</div></div>`;
      return html;
    };

    const requiredHtml = buildSection(requiredSubs, 'Required', '#b91c1c', '#fef2f2', '#16a34a');

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>Cleaning Checklist — ${unitLine || 'Unit'}</title>
<style>
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { size: portrait; margin: 0.5in; } }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; padding: 40px; font-size: 13px; line-height: 1.5; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #dc2626; }
  .logo { font-size: 22px; font-weight: 700; color: #dc2626; }
  .meta { text-align: right; font-size: 12px; color: #6b7280; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
  .info-label { font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 2px; }
  .info-value { font-size: 13px; font-weight: 500; margin: 0; }
  .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; text-align: center; }
</style>
</head><body>
  <div class="header">
    <div>
      <div class="logo">Amber Peak</div>
      <div style="font-size:12px;color:#6b7280;margin-top:2px">Cleaning Checklist${unitLine ? ` — ${unitLine}` : ''}</div>
    </div>
    <div class="meta">
      <div>Generated ${generatedDate}</div>
    </div>
  </div>

  <div class="info-grid">
    <div><p class="info-label">Unit</p><p class="info-value">${unitLine || '\u2014'}</p></div>
    <div><p class="info-label">Lease End</p><p class="info-value">${leaseEndDate ? formatDateOnly(leaseEndDate) : '\u2014'}</p></div>
  </div>

  ${requiredHtml}

  <div class="footer">
    This checklist represents the condition requirements for returning your unit in broom-swept condition. For questions, contact your property management office.
  </div>
</body></html>`;

    const printWindow = window.open('', '_blank', 'width=800,height=1100');
    if (!printWindow) {
      alert('Please allow popups to export your checklist');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);
  };

  const CREDIT_TYPES = useMemo(() => new Set(['Payment', 'Credit', 'ApplyDeposit', 'ElectronicFundsTransfer']), []);

  const transactionsWithBalance = useMemo(() => {
    if (!ledger || ledger.transactions.length === 0) return [];
    const oldest = [...ledger.transactions].reverse();
    let running = 0;
    const mapped = oldest.map((tx) => {
      if (CREDIT_TYPES.has(tx.type)) {
        running -= Math.abs(tx.totalAmount);
      } else {
        running += Math.abs(tx.totalAmount);
      }
      return { ...tx, runningBalance: Math.round(running * 100) / 100 };
    });
    return mapped.reverse();
  }, [ledger, CREDIT_TYPES]);

  const [leaseEndCopied, setLeaseEndCopied] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const [utilitiesUploadError, setUtilitiesUploadError] = useState<string | null>(null);
  const [utilitiesConverting, setUtilitiesConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const internetFileInputRef = useRef<HTMLInputElement | null>(null);

  const todayET = getEasternToday();
  const leaseEndDate = moveOut?.leaseEndDate ?? null;
  const dateWindow = leaseEndDate ? buildDateWindow(leaseEndDate, weekOffset) : [];
  const canGoForward = weekOffset < 0;
  const hasAppointment = moveOut?.appointmentStartTime != null;
  /** False when within 72h before noon ET on lease end (from server). Undefined treated as allowed. */
  const selfServiceOk = moveOut?.bookingSelfServiceAvailable !== false;

  useEffect(() => {
    if (hasAppointment) {
      setMode('confirmed');
    } else {
      setMode('picker');
    }
  }, [hasAppointment]);

  useEffect(() => {
    if (moveOut?.furnitureSaleSellerConsent) {
      setFurnitureSaleSellerConsent(true);
      setFurnitureSaleSubmitted(true);
    }
  }, [moveOut?.furnitureSaleSellerConsent]);

  // Auto-uncheck furniture confirmation when prerequisites (proof + photos) aren't met.
  // This catches stale DB records from previous sessions and ensures the user must
  // always manually confirm after prerequisites are satisfied.
  useEffect(() => {
    const hasProof = !!(furnitureProof || moveOut?.furnitureSaleProofFilename);
    const hasPhotos = saleItems.length > 0;
    if (hasProof && hasPhotos) return;

    setChecklistItems((prev) => {
      const confirmed = prev.find((ci) => ci.itemKey === 'furniture-items-confirmed');
      if (!confirmed) return prev;
      if (moveOut?.id) {
        toggleChecklistItem(getToken, 'furniture-items-confirmed', undefined, moveOut.id).catch(() => {});
      }
      return prev.filter((ci) => ci.itemKey !== 'furniture-items-confirmed');
    });
  }, [furnitureProof, moveOut?.furnitureSaleProofFilename, saleItems.length, moveOut?.id, getToken]);

  // Furniture items + proof are fetched in the parallel batch below (moveOutParallelFetch)

  const handleProofUpload = useCallback(async (file: File) => {
    if (!moveOut?.id) return;
    setFurnitureProofUploading(true);
    try {
      const proof = await uploadFurnitureProof(getToken, moveOut.id, file);
      setFurnitureProof(proof);
    } catch (err) {
      console.error('Failed to upload furniture proof', err);
    } finally {
      setFurnitureProofUploading(false);
    }
  }, [moveOut?.id, getToken]);

  const handleProofDelete = useCallback(async () => {
    if (!moveOut?.id) return;
    setFurnitureProofDeleting(true);
    try {
      await deleteFurnitureProof(getToken, moveOut.id);
      setFurnitureProof(null);
      await refreshProfile();
      const confirmed = checklistItems.find((ci) => ci.itemKey === 'furniture-items-confirmed');
      if (confirmed) {
        setChecklistItems((prev) => prev.filter((ci) => ci.itemKey !== 'furniture-items-confirmed'));
        toggleChecklistItem(getToken, 'furniture-items-confirmed', undefined, moveOut.id).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to delete furniture proof', err);
    } finally {
      setFurnitureProofDeleting(false);
    }
  }, [moveOut?.id, getToken, refreshProfile, checklistItems]);

  const uploadSaleItem = useCallback(async (file: File, name: string, description?: string) => {
    if (!moveOut?.id) return;
    const { item, uploadUrl } = await createFurnitureSaleItem(
      getToken,
      moveOut.id,
      name,
      file,
      description || undefined
    );
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    const refreshed = await getFurnitureSaleItems(getToken, moveOut?.id);
    setSaleItems(refreshed);
  }, [moveOut?.id, getToken]);

  const handleDeleteSaleItem = useCallback(async (itemId: string) => {
    try {
      await deleteFurnitureSaleItem(getToken, itemId);
      setSaleItems((prev) => {
        const remaining = prev.filter((i) => i.id !== itemId);
        if (remaining.length === 0) {
          const confirmed = checklistItems.find((ci) => ci.itemKey === 'furniture-items-confirmed');
          if (confirmed) {
            setChecklistItems((p) => p.filter((ci) => ci.itemKey !== 'furniture-items-confirmed'));
            const moId = moveOut?.id;
            if (moId) toggleChecklistItem(getToken, 'furniture-items-confirmed', undefined, moId).catch(() => {});
          }
        }
        return remaining;
      });
    } catch (err) {
      console.error('Failed to delete furniture sale item', err);
    }
  }, [getToken, checklistItems, moveOut?.id]);

  const handleFilesSelected = useCallback(async (files: FileList | File[]) => {
    const images = Array.from(files).filter(isAcceptedImage);
    if (images.length === 0) return;

    const hasHeic = images.some(isHeicFile);
    if (hasHeic) {
      setHeicConverting(true);
      try {
        const converted = await Promise.all(
          images.map((f) => (isHeicFile(f) ? convertHeicToJpeg(f) : f))
        );
        setPendingFiles(converted);
      } catch (err) {
        console.error('HEIC conversion failed', err);
        setPendingFiles(images.filter((f) => !isHeicFile(f)));
      } finally {
        setHeicConverting(false);
      }
    } else {
      setPendingFiles(images);
    }

    setTaggingIndex(0);
    setTaggingName('');
    setTaggingDescription('');
  }, []);

  const handleTaggingSave = useCallback(async () => {
    const file = pendingFiles[taggingIndex];
    if (!file || !taggingName.trim()) return;
    setTaggingUploading(true);
    try {
      await uploadSaleItem(file, taggingName.trim(), taggingDescription.trim());
    } catch (err) {
      console.error('Failed to upload furniture sale item', err);
    } finally {
      setTaggingUploading(false);
    }
    if (taggingIndex < pendingFiles.length - 1) {
      setTaggingIndex((i) => i + 1);
      setTaggingName('');
      setTaggingDescription('');
    } else {
      setPendingFiles([]);
      setTaggingIndex(0);
      setTaggingName('');
      setTaggingDescription('');
    }
  }, [pendingFiles, taggingIndex, taggingName, taggingDescription, uploadSaleItem]);

  const handleTaggingSkip = useCallback(() => {
    if (pendingFiles.length <= 1) {
      setPendingFiles([]);
      setTaggingIndex(0);
    } else {
      setPendingFiles((prev) => prev.filter((_, i) => i !== taggingIndex));
      setTaggingIndex((i) => Math.min(i, pendingFiles.length - 2));
    }
    setTaggingName('');
    setTaggingDescription('');
  }, [pendingFiles, taggingIndex]);

  const handleTaggingCancelAll = useCallback(() => {
    setPendingFiles([]);
    setTaggingIndex(0);
    setTaggingName('');
    setTaggingDescription('');
  }, []);

  // Edit existing sale item state
  const [editingItem, setEditingItem] = useState<FurnitureSaleItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const openEditItem = useCallback((item: FurnitureSaleItem) => {
    setEditingItem(item);
    setEditName(item.name);
    setEditDescription(item.description ?? '');
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editingItem || !editName.trim()) return;
    setEditSaving(true);
    try {
      await updateFurnitureSaleItem(getToken, editingItem.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      const refreshed = await getFurnitureSaleItems(getToken, moveOut?.id);
      setSaleItems(refreshed);
      setEditingItem(null);
    } catch (err) {
      console.error('Failed to update sale item', err);
    } finally {
      setEditSaving(false);
    }
  }, [editingItem, editName, editDescription, getToken]);

  const handleEditCancel = useCallback(() => {
    setEditingItem(null);
    setEditName('');
    setEditDescription('');
  }, []);

  useEffect(() => {
    if (!editingItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleEditCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingItem, handleEditCancel]);

  // Always fetch fresh content in the background (stale-while-revalidate).
  // If we had a cache hit above, the video is already rendering; this just refreshes the cache.
  const contentFetchedRef = useRef(false);
  if (!contentFetchedRef.current) {
    contentFetchedRef.current = true;
    getMoveOutContent(getToken)
      .then((data) => {
        if (import.meta.env.DEV) {
          console.log('[MoveOut] content fetch ok', { count: data?.length ?? 0, sections: data?.map((c) => c.sectionName) });
        }
        const items = data ?? [];
        setPageContent(items);
        setContentLoadError(null);
        writeCachedContent(items);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to load videos and images';
        if (import.meta.env.DEV) {
          console.error('[MoveOut] content fetch failed', err);
        }
        if (pageContent.length === 0) {
          setContentLoadError(message);
        }
      })
      .finally(() => setContentLoading(false));
  }

  const refetchChecklist = useCallback(async (id?: string) => {
    try {
      const data = await getMoveOutChecklist(getToken, id);
      setChecklistMoveOutId(data.moveOutId);
      setChecklistItems(data.items);
    } catch { /* swallow */ }
  }, [getToken]);

  const fetchDisposalWorkOrders = useCallback(() => {
    if (!moveOut?.id) return;
    getDisposalWorkOrders(getToken, moveOut.id)
      .then(setDisposalWorkOrders)
      .catch(() => setDisposalWorkOrders([]));
  }, [moveOut?.id, getToken]);

  // --- Parallel data fetch: acquire token once, fire all move-out queries together ---
  const moveOutFetchedRef = useRef(false);
  useEffect(() => {
    if (!moveOut?.id) return;
    if (moveOutFetchedRef.current) return;
    moveOutFetchedRef.current = true;

    (async () => {
      const token = await getToken();
      const withToken = () => Promise.resolve(token);
      const id = moveOut.id;

      const [checklist, ledger, disposal, items, proof] = await Promise.allSettled([
        getMoveOutChecklist(withToken, id),
        getMoveOutLedger(withToken, id),
        getDisposalWorkOrders(withToken, id),
        getFurnitureSaleItems(withToken, id),
        moveOut.furnitureSaleProofFilename ? getFurnitureProof(withToken) : Promise.resolve(null),
      ]);

      if (checklist.status === 'fulfilled') {
        setChecklistMoveOutId(checklist.value.moveOutId);
        setChecklistItems(checklist.value.items);
      }
      setChecklistLoading(false);

      setLedger(ledger.status === 'fulfilled' ? ledger.value : null);
      setLedgerLoading(false);

      setDisposalWorkOrders(disposal.status === 'fulfilled' ? disposal.value : []);

      if (items.status === 'fulfilled') setSaleItems(items.value);
      setSaleItemsLoading(false);

      if (proof.status === 'fulfilled' && proof.value) setFurnitureProof(proof.value);
    })();
  }, [moveOut?.id, getToken]);

  const handleDeleteDisposalWO = useCallback(async (workOrderId: string) => {
    setDisposalDeleting(workOrderId);
    try {
      await deleteDisposalWorkOrder(getToken, workOrderId);
      setDisposalWorkOrders((prev) => prev.filter((wo) => wo.id !== workOrderId));
      setDisposalDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete disposal work order', err);
    } finally {
      setDisposalDeleting(null);
    }
  }, [getToken]);

  const getContentForSection = useCallback(
    (sectionName: string) => pageContent.filter((c) => c.sectionName === sectionName),
    [pageContent]
  );

  const loadAllSlots = useCallback(async () => {
    if (dateWindow.length === 0) return;
    setCalendarLoading(true);
    setError(null);
    try {
      const futureDates = dateWindow.filter((d) => d >= todayET);
      if (futureDates.length === 0) {
        setSlotsByDate({});
        return;
      }
      const slotMap = await getMoveOutSlotsForDates(getToken, futureDates, moveOut?.id ?? undefined);
      setSlotsByDate(filterMoveOutSlotsForDisplay(slotMap, moveOut?.leaseEndDate));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load availability');
      setSlotsByDate({});
    } finally {
      setCalendarLoading(false);
    }
  }, [dateWindow.join(','), todayET, getToken, moveOut?.id, moveOut?.leaseEndDate]);

  useEffect(() => {
    if (mode === 'picker' && bookingOpen && selfServiceOk && dateWindow.length > 0) {
      loadAllSlots();
    }
  }, [mode, bookingOpen, selfServiceOk, dateWindow.join(','), loadAllSlots]);

  const handleBook = async (slot: SlotInfo) => {
    if (!moveOut) return;
    setBooking(true);
    setError(null);
    try {
      await bookMoveOutSlot(getToken, moveOut.id, slot.startTime);
      await refreshProfile();
      await refetchChecklist(moveOut.id);
      setMode('confirmed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Booking failed');
      loadAllSlots();
    } finally {
      setBooking(false);
    }
  };

  const handleReschedule = () => {
    if (!selfServiceOk) return;
    setMode('picker');
    setSlotsByDate({});
  };

  const handleRequestService = async (serviceType: 'furniture-removal' | 'garbage-pickup', preferredDate: string) => {
    if (!moveOut) return;
    setServiceRequesting(serviceType);
    try {
      const result = await requestDisposalService(getToken, serviceType, preferredDate, moveOut.id);
      if (result.sent || result.workOrder?.referenceNumber) {
        setServiceRequested((prev) => new Set(prev).add(serviceType));
        setServicePickingDate(null);
        setServiceDate('');
        if (result.workOrder?.referenceNumber) {
          setServiceWorkOrderRefs((prev) => ({ ...prev, [serviceType]: result.workOrder!.referenceNumber! }));
        }
        fetchDisposalWorkOrders();
      } else {
        setError('Your request could not be delivered at this time. Please try again or contact us directly.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setServiceRequesting(null);
    }
  };

  // Filter page sections based on move-out settings
  const visibleSections = useMemo(() => {
    return PAGE_SECTIONS.filter((s) => {
      if (s.id === 'google-review' && !moveOut?.askForGoogleReview) return false;
      return true;
    });
  }, [moveOut?.askForGoogleReview]);

  // Track which section is currently in view
  const [activeSection, setActiveSection] = useState<string | null>(PAGE_SECTIONS[0]?.id ?? null);

  useEffect(() => {
    const sectionEls = visibleSections.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    if (sectionEls.length === 0) return;

    const scrollRoot = document.querySelector('main');

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { root: scrollRoot, rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // ---------------------------------------------------------------------------
  // Checklist helpers — template-driven, records created/deleted on interact
  // ---------------------------------------------------------------------------

  const getTask = (key: string) => mergedTasks.find((t) => t.itemKey === key);

  const furnitureConfirmedTask = getTask('furniture-items-confirmed');
  const utilitiesTask = getTask('utilities-closed');
  const internetDisconnectedTask = getTask('internet-disconnected');
  const internetRouterTask = getTask('internet-router-returned');

  // Local checkbox state for dual-requirement tasks (checkbox + attachment)
  const [utilitiesChecked, setUtilitiesChecked] = useState(false);
  const [internetDisconnectedChecked, setInternetDisconnectedChecked] = useState(false);

  useEffect(() => {
    const dbItem = checklistItems.find((ci) => ci.itemKey === 'utilities-closed');
    setUtilitiesChecked(!!dbItem);
  }, [checklistItems]);

  useEffect(() => {
    const dbItem = checklistItems.find((ci) => ci.itemKey === 'internet-disconnected');
    setInternetDisconnectedChecked(!!dbItem);
  }, [checklistItems]);

  const syncTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const syncInFlightRef = useRef<Set<string>>(new Set());
  const syncGenRef = useRef<Map<string, number>>(new Map());

  const handleToggleTask = (
    itemKey: string,
    meta?: { type?: string; label?: string; category?: string; sortOrder?: number }
  ) => {
    setChecklistItems((prev) => {
      const existing = prev.find((ci) => ci.itemKey === itemKey);
      if (existing) {
        return prev.filter((ci) => ci.itemKey !== itemKey);
      }
      const optimistic: ChecklistItem = {
        id: `optimistic-${itemKey}`,
        type: meta?.type ?? 'GENERAL',
        itemKey,
        label: meta?.label ?? itemKey,
        category: meta?.category ?? null,
        completed: true,
        completedAt: new Date().toISOString(),
        sortOrder: meta?.sortOrder ?? 0,
        metadata: null,
        attachment: null,
      };
      return [...prev, optimistic];
    });

    const gen = (syncGenRef.current.get(itemKey) || 0) + 1;
    syncGenRef.current.set(itemKey, gen);

    const existing = syncTimerRef.current.get(itemKey);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      syncTimerRef.current.delete(itemKey);
      if (syncInFlightRef.current.has(itemKey)) return;

      const capturedGen = syncGenRef.current.get(itemKey) || 0;
      syncInFlightRef.current.add(itemKey);
      try {
        const moId = moveOut?.id ?? checklistMoveOutId ?? undefined;
        const result = await toggleChecklistItem(getToken, itemKey, meta, moId);

        if (syncGenRef.current.get(itemKey) !== capturedGen) return;

        if (result.action === 'checked' && result.item) {
          setChecklistItems((prev) => {
            const had = prev.some((ci) => ci.itemKey === itemKey);
            if (!had) return [...prev, result.item as ChecklistItem];
            return prev.map((ci) => ci.itemKey === itemKey ? (result.item as ChecklistItem) : ci);
          });
        } else if (result.action === 'unchecked') {
          setChecklistItems((prev) => prev.filter((ci) => ci.itemKey !== itemKey));
        }
      } catch (err) {
        console.error(`[toggle] "${itemKey}" — server sync failed`, err);
      } finally {
        syncInFlightRef.current.delete(itemKey);
      }
    }, 400);
    syncTimerRef.current.set(itemKey, timer);
  };

  const handleToggleSimpleTask = (
    itemKey: string,
    meta?: { type?: string; label?: string; category?: string; sortOrder?: number }
  ) => {
    handleToggleTask(itemKey, meta);

    const isCleaningSub = requiredSubs.some((s) => s.itemKey === itemKey);
    if (isCleaningSub) {
      const subCurrentlyCompleted = requiredSubs.find((s) => s.itemKey === itemKey)?.completed;
      const unitCleanedTask = getTask('unit-cleaned');
      if (subCurrentlyCompleted && unitCleanedTask?.completed) {
        handleToggleTask('unit-cleaned');
      }
    }
  };

  const handleToggleDualTask = (itemKey: string) => {
    if (itemKey === 'utilities-closed') setUtilitiesChecked((c) => !c);
    if (itemKey === 'internet-disconnected') setInternetDisconnectedChecked((c) => !c);

    setChecklistItems((prev) => {
      const existing = prev.find((ci) => ci.itemKey === itemKey);
      if (existing) return prev.filter((ci) => ci.itemKey !== itemKey);
      const optimistic: ChecklistItem = {
        id: `optimistic-${itemKey}`,
        type: 'GENERAL',
        itemKey,
        label: itemKey,
        category: null,
        completed: false,
        completedAt: null,
        sortOrder: 0,
        metadata: null,
        attachment: null,
      };
      return [...prev, optimistic];
    });

    const gen = (syncGenRef.current.get(itemKey) || 0) + 1;
    syncGenRef.current.set(itemKey, gen);

    const existingTimer = syncTimerRef.current.get(itemKey);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(async () => {
      syncTimerRef.current.delete(itemKey);
      if (syncInFlightRef.current.has(itemKey)) return;

      const capturedGen = syncGenRef.current.get(itemKey) || 0;
      syncInFlightRef.current.add(itemKey);
      try {
        const moId = moveOut?.id ?? checklistMoveOutId ?? undefined;
        const result = await toggleChecklistItem(getToken, itemKey, undefined, moId);

        if (syncGenRef.current.get(itemKey) !== capturedGen) return;

        if (result.action === 'checked' && result.item) {
          setChecklistItems((prev) => {
            const exists = prev.some((ci) => ci.itemKey === itemKey);
            if (exists) return prev.map((ci) => ci.itemKey === itemKey ? (result.item as ChecklistItem) : ci);
            return [...prev, result.item as ChecklistItem];
          });
        } else {
          setChecklistItems((prev) => prev.filter((ci) => ci.itemKey !== itemKey));
        }
      } catch (err) {
        console.error(`[toggle-dual] "${itemKey}" — server sync failed`, err);
      } finally {
        syncInFlightRef.current.delete(itemKey);
      }
    }, 400);
    syncTimerRef.current.set(itemKey, timer);
  };

  const handleCopyLeaseEnd = () => {
    if (!leaseEndDate) return;
    navigator.clipboard.writeText(leaseEndDate).then(() => {
      setLeaseEndCopied(true);
      setTimeout(() => setLeaseEndCopied(false), 2000);
    });
  };

  const handleUploadAttachment = async (task: MergedTask, file: File) => {
    let itemId = task.dbId;
    if (!itemId) {
      const moId = moveOut?.id ?? checklistMoveOutId ?? undefined;
      const result = await toggleChecklistItem(getToken, task.itemKey, undefined, moId);
      if (result.action === 'checked' && result.item) {
        itemId = result.item.id;
        setChecklistItems((prev) => [...prev, result.item as ChecklistItem]);
        if (task.itemKey === 'utilities-closed') setUtilitiesChecked(true);
        if (task.itemKey === 'internet-disconnected') setInternetDisconnectedChecked(true);
      }
    }
    if (!itemId) return;
    setUploadingItemId(itemId);
    try {
      const { uploadUrl, s3Key, s3Bucket } = await getChecklistUploadUrl(
        getToken, itemId, file.name, file.type, file.size
      );
      const putRes = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!putRes.ok) throw new Error('Upload failed');
      const result = await confirmChecklistAttachment(getToken, itemId, {
        s3Key, s3Bucket, mimeType: file.type, size: file.size, filename: file.name,
      });
      setChecklistItems((prev) =>
        prev.map((ci) => (ci.id === itemId ? { ...ci, completed: result.completed, completedAt: result.completedAt, attachment: result.attachment } : ci))
      );
    } catch (err) {
      console.error('Attachment upload failed', err);
    } finally {
      setUploadingItemId(null);
    }
  };

  const formatDateOnly = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleDownloadRentReceipt = useCallback(() => {
    if (!ledger || transactionsWithBalance.length === 0) return;
    const tenantName = tenant ? `${tenant.firstName} ${tenant.lastName}` : 'Tenant';
    const lease = tenant?.residentialLease;
    const unitLine = [lease?.unitNumber, lease?.propertyName].filter(Boolean).join(' — ') || '';
    const addressLine = lease?.address || '';
    const leasePeriod = lease?.leaseFrom && lease?.leaseTo
      ? `${formatDateOnly(lease.leaseFrom)} — ${formatDateOnly(lease.leaseTo)}`
      : '';
    const generatedDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });

    const rows = transactionsWithBalance.map((tx) => {
      const isCredit = CREDIT_TYPES.has(tx.type);
      const amountStr = `${isCredit ? '-' : ''}$${Math.abs(tx.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      const amtColor = isCredit ? '#15803d' : '#111827';
      const balColor = tx.runningBalance > 0 ? '#b91c1c' : tx.runningBalance < 0 ? '#15803d' : '#111827';
      const balStr = `$${Math.abs(tx.runningBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}${tx.runningBalance < 0 ? ' CR' : ''}`;
      return `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;white-space:nowrap">${tx.date}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${tx.type}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280">${tx.memo || '—'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:500;color:${amtColor};white-space:nowrap">${amountStr}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;color:${balColor};white-space:nowrap">${balStr}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>Rent Payment Receipt — ${tenantName}</title>
<style>
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { size: portrait; margin: 0.5in; } }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; padding: 40px; font-size: 13px; line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #dc2626; }
  .logo svg { height: 36px; width: auto; }
  .meta { text-align: right; font-size: 12px; color: #6b7280; }
  .section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin-bottom: 8px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
  .info-block p { margin: 2px 0; }
  .info-label { font-size: 11px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.04em; }
  .info-value { font-size: 14px; font-weight: 500; }
  th { text-align: left; padding: 8px 12px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; border-bottom: 2px solid #e5e7eb; background: #f9fafb; }
  th.right { text-align: right; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; text-align: center; }
</style>
</head><body>
  <div class="header">
    <div>
      <div class="logo"><svg width="1600" height="266" viewBox="0 0 1600 266" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><path fill-rule="evenodd" clip-rule="evenodd" d="M106.112 11.192C113.535 3.76753 123.292 0 133.043 0C142.794 0 152.551 3.76753 159.974 11.192L235.218 88.181C249.559 102.524 258.031 111.08 262.222 122.704C266.104 133.467 266.097 145.935 266.084 167.282C266.084 168.527 266.083 169.802 266.083 171.109V194.07C266.083 202.294 255.629 205.805 250.665 199.248L218.213 156.387C214.8 151.879 208.035 151.848 204.581 156.325L167.667 204.177C164.183 208.693 157.345 208.615 153.965 204.022L143.041 189.176C139.66 184.581 132.809 184.493 129.208 188.918C119.105 201.333 106.552 215.714 95.094 228.842L95.0934 228.843L95.0923 228.844C83.1105 242.572 72.3254 254.929 66.788 262.232C65.0097 264.578 62.2762 266.083 59.3329 266.083H38.0329C17.1405 266.083 0.00288961 249.206 0.00288961 228.053V171.109C0.00288961 169.802 0.00212812 168.525 0.00138507 167.28L0.001385 167.28C-0.0112121 145.934-0.0185695 133.467 3.86344 122.704C8.05561 111.08 16.527 102.524 30.8686 88.181L106.112 11.192ZM747.693 106.09C755.416 95.0897 770.395 90.1749 782.799 90.1749C810.884 90.1749 836.394 112.409 836.394 147.515C836.394 182.62 810.884 205.088 782.799 205.088C770.395 205.088 755.416 199.939 747.693 188.705V202.28H717.034V33.5374H747.693V106.09ZM748.629 147.515C748.629 164.131 761.969 176.535 776.948 176.535C792.395 176.535 805.033 163.429 805.033 147.515C805.033 131.366 792.395 118.728 776.948 118.728C762.672 118.728 748.629 130.664 748.629 147.515ZM470.046 107.494C462.79 96.2599 448.748 90.1749 434.706 90.1749C403.344 90.1749 381.345 113.813 381.345 147.749C381.345 181.684 403.813 205.088 434.004 205.088C448.748 205.088 462.79 197.833 470.046 186.599V202.28H500.705V92.9834H470.046V107.494ZM440.791 176.535C424.642 176.535 412.706 162.961 412.706 147.749C412.706 131.834 424.642 118.728 440.557 118.728C455.769 118.728 469.109 131.132 469.109 147.749C469.109 163.429 456.237 176.535 440.791 176.535ZM655.465 90.1749C643.997 90.1749 626.912 95.7919 618.955 111.707C612.402 97.8982 599.998 90.1749 583.849 90.1749C571.445 90.1749 555.296 98.1322 552.956 114.281V92.9834H522.297V202.28H552.956V144.706C552.956 125.047 564.19 118.962 575.19 118.962C585.487 118.962 594.381 126.451 594.381 143.77V202.28H625.04V148.451C625.04 128.089 633.465 118.728 646.572 118.962C657.337 118.962 666.231 126.217 666.231 143.302V202.28H697.124V142.366C697.124 109.6 682.848 90.1749 655.465 90.1749ZM959.793 145.876C959.793 112.409 934.283 90.1749 902.921 90.1749C871.794 90.1749 844.88 112.409 844.88 147.749C844.88 181.45 869.454 205.088 903.155 205.088C922.347 205.088 941.304 196.897 951.602 183.089L933.815 165.067C927.027 172.791 916.262 177.94 905.028 177.94C890.517 177.94 880.22 170.45 876.943 157.578H959.091C959.559 152.663 959.793 148.919 959.793 145.876ZM876.943 137.685C880.22 122.94 890.985 116.621 903.623 116.621C917.432 116.621 927.964 124.813 928.9 137.685H876.943ZM1004.6 116.387C1010.69 98.1322 1023.09 90.1749 1036.66 90.1749C1042.51 90.1749 1049.07 91.1111 1053.98 93.6855L1046.96 122.94C1040.41 119.43 1035.03 118.962 1031.28 118.962C1015.6 118.962 1004.83 133.706 1004.83 164.833V202.28H974.175V92.9834H1004.6V116.387ZM1167.03 90.1749C1154.63 90.1749 1139.65 95.3238 1131.93 106.324V92.9834H1101.27V255.875H1131.93V188.705C1139.65 199.939 1154.63 205.088 1167.03 205.088C1195.12 205.088 1220.63 182.854 1220.63 147.515C1220.63 112.643 1195.12 90.1749 1167.03 90.1749ZM1161.18 176.535C1146.91 176.535 1132.86 164.365 1132.86 147.749C1132.86 131.132 1146.21 118.728 1161.18 118.728C1176.63 118.728 1189.27 131.6 1189.27 147.749C1189.27 163.897 1176.63 176.535 1161.18 176.535ZM1287.16 90.1749C1318.52 90.1749 1344.03 112.409 1344.03 145.876C1344.03 148.919 1343.79 152.663 1343.33 157.578H1261.18C1264.46 170.45 1274.75 177.94 1289.26 177.94C1300.5 177.94 1311.26 172.791 1318.05 165.067L1335.84 183.089C1325.54 196.897 1306.58 205.088 1287.39 205.088C1253.69 205.088 1229.12 181.45 1229.12 147.749C1229.12 112.409 1256.03 90.1749 1287.16 90.1749ZM1287.86 116.621C1275.22 116.621 1264.46 122.94 1261.18 137.685H1313.14C1312.2 124.813 1301.67 116.621 1287.86 116.621ZM1440.56 107.494C1433.3 96.2599 1419.26 90.1749 1405.22 90.1749C1373.86 90.1749 1351.86 113.813 1351.86 147.749C1351.86 181.684 1374.33 205.088 1404.52 205.088C1419.26 205.088 1433.3 197.833 1440.56 186.599V202.28H1471.22V92.9834H1440.56V107.494ZM1411.3 176.535C1395.16 176.535 1383.22 162.961 1383.22 147.749C1383.22 131.834 1395.16 118.728 1411.07 118.728C1426.28 118.728 1439.62 131.132 1439.62 147.749C1439.62 163.429 1426.75 176.535 1411.3 176.535ZM1590.4 92.9834L1547.81 141.195L1600 202.28H1561.85L1523.47 155.004V202.28H1492.81V33.5374H1523.47V126.451L1551.09 92.9834H1590.4Z" fill="#E12E07"/></g><defs><clipPath id="clip0"><rect width="1600" height="266" fill="white"/></clipPath></defs></svg></div>
      <div style="font-size:12px;color:#6b7280;margin-top:2px">Rent Payment Receipt</div>
    </div>
    <div class="meta">
      <div>Generated ${generatedDate}</div>
      <div style="margin-top:4px"><strong style="color:#111827">Amber Peak Property Management</strong></div>
    </div>
  </div>

  <div class="info-grid">
    <div class="info-block">
      <p class="info-label">Tenant</p>
      <p class="info-value">${tenantName}</p>
    </div>
    <div class="info-block">
      <p class="info-label">Unit</p>
      <p class="info-value">${unitLine || '—'}</p>
    </div>
    <div class="info-block">
      <p class="info-label">Property Address</p>
      <p class="info-value">${addressLine || '—'}</p>
    </div>
    <div class="info-block">
      <p class="info-label">Lease Period</p>
      <p class="info-value">${leasePeriod || '—'}</p>
    </div>
  </div>

  <div class="section-title">Transaction History</div>
  <table>
    <thead><tr>
      <th>Date</th><th>Type</th><th>Memo</th><th class="right">Amount</th><th class="right">Balance</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer">
    This document is generated for your records. For questions, please contact your property management office.
  </div>
</body></html>`;

    const printWindow = window.open('', '_blank', 'width=800,height=1100');
    if (!printWindow) {
      alert('Please allow popups to download your receipt');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);
  }, [ledger, transactionsWithBalance, tenant, CREDIT_TYPES]);

  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);

  const handleDeleteAttachment = async (task: MergedTask) => {
    if (!task.dbId) return;
    setDeletingAttachmentId(task.dbId);
    try {
      await deleteChecklistAttachment(getToken, task.dbId);
      const isDual = DUAL_TASK_ITEM_KEYS.has(task.itemKey);
      setChecklistItems((prev) =>
        prev.map((ci) => (ci.id === task.dbId
          ? { ...ci, attachment: null, ...(isDual ? { completed: false, completedAt: null } : {}) }
          : ci))
      );
    } catch (err) {
      console.error('Attachment delete failed', err);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const hasProof = !!(furnitureProof || moveOut?.furnitureSaleProofFilename);
  const furnitureTaskComplete = hasProof && saleItems.length > 0 && !!furnitureConfirmedTask?.completed;
  const summaryTasks = useMemo(() => {
    const tasks = mergedTasks
      .filter((t) => t.itemKey !== 'furniture-items-confirmed')
      .map((t) => {
        if (t.itemKey === 'appointment-booked' && !hasAppointment) {
          return { ...t, completed: false };
        }
        return t;
      });
    if (furnitureSaleSubmitted) {
      tasks.push({
        itemKey: 'furniture-sale-complete',
        type: 'FURNITURE',
        label: 'Complete all furniture sale activities',
        category: 'Furniture',
        sortOrder: 6.5,
        completed: furnitureTaskComplete,
        completedAt: null,
        attachment: null,
        dbId: null,
        metadata: null,
      });
    }
    return tasks;
  }, [mergedTasks, furnitureSaleSubmitted, furnitureTaskComplete, hasAppointment]);

  const totalTasks = summaryTasks.length;
  const completedTasks = summaryTasks.filter((t) => t.completed).length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const allTasksDone = totalTasks > 0 && completedTasks === totalTasks;

  // Page-level loading gate: wait for all database-driven data before rendering
  // When there's no move-out record, skip waiting for move-out-specific data
  const pageReady = !profileLoading && !contentLoading &&
    (!moveOut?.id || (!checklistLoading && !ledgerLoading && !saleItemsLoading));

  const prevAllDoneRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevAllDoneRef.current === null) {
      prevAllDoneRef.current = allTasksDone;
      return;
    }
    if (allTasksDone && !prevAllDoneRef.current) {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#b91c1c', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#b91c1c', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'],
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();

      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { x: 0.5, y: 0.4 },
          colors: ['#b91c1c', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'],
        });
      }, 500);
    }
    prevAllDoneRef.current = allTasksDone;
  }, [allTasksDone]);

  if (!pageReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-full border-4 border-gray-200 dark:border-white/10" />
          <div className="absolute inset-0 w-14 h-14 rounded-full border-4 border-red-700 border-t-transparent animate-spin" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Preparing your move-out portal</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Loading your information…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SectionTracker sections={visibleSections} activeId={activeSection} />

      <div className="w-full sm:w-[94%] md:w-[92%] xl:w-[88%] 2xl:w-[76%] max-w-5xl mx-auto space-y-6">
        <div data-sticky-header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm rounded-b-2xl shadow-md border border-gray-200 dark:border-white/10 px-5 py-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <PackageOpen size={28} className="text-red-700 dark:text-red-400" />
            Move Out
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Everything you need to know about your upcoming move-out.
          </p>

          {totalTasks > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {completedTasks} of {totalTasks} tasks complete
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => scrollToSection('task-summary')}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-full px-3 py-1 hover:bg-red-100 hover:border-red-300 transition-all shadow-sm"
                  >
                    <ListChecks size={12} />
                    Return to Summary
                  </button>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{progressPct}%</span>
                </div>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-red-700 transition-all duration-500 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Section: Welcome */}
        <section id="welcome">
          <AutoPlayVideoPlayer items={getContentForSection('welcome-video')} placeholderLabel="Move-Out Guide Video" />
        </section>

        {/* Task summary */}
        <div id="task-summary">
          {moveOut && !checklistLoading && <TaskSummary items={summaryTasks} onNavigate={(sectionId) => {
            setForceOpenId((prev) => ({ sectionId, rev: prev.rev + 1 }));
            setTimeout(() => scrollToSection(sectionId), 50);
          }} />}
        </div>

        {moveOut && (
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={expandAll} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 transition-all">
              <ChevronsDown size={13} />
              Expand All Sections
            </button>
            <button type="button" onClick={collapseAll} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-white/5 hover:border-gray-300 transition-all">
              <ChevronsUp size={13} />
              Collapse All Sections
            </button>
          </div>
        )}

        {/* Section: Book Your Appointment */}
        {moveOut && (
          <section id="book-appointment">
            <CollapsibleSection number={1} title="Book Your Move-Out Appointment" isComplete={mode === 'confirmed'} forceToggle={sectionToggle} sectionId="book-appointment" forceOpenId={forceOpenId}>

            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Move-Out FAQs</h3>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 px-5 py-2">
              <FaqAccordion items={MOVE_OUT_FAQ} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-6 space-y-5">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/40 rounded-lg">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              {mode === 'confirmed' && moveOut.appointmentStartTime && moveOut.appointmentEndTime && (
                <ConfirmedCard
                  startTime={moveOut.appointmentStartTime}
                  endTime={moveOut.appointmentEndTime}
                  onReschedule={handleReschedule}
                  lease={tenant?.residentialLease}
                  selfServiceAvailable={selfServiceOk}
                />
              )}

              {mode === 'picker' && !bookingOpen && (
                <div className="text-center py-10 space-y-4">
                  <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                    <Lock size={28} className="text-amber-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Appointment Booking Not Yet Available</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Move-out appointment booking will go live on
                    </p>
                    <p className="text-base font-semibold text-gray-900 dark:text-white mt-1">
                      Monday, March 23 at 12:00 PM noon ET
                    </p>
                  </div>
                  {countdown && (
                    <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <Clock size={14} className="shrink-0" />
                      <span>
                        {countdown.days > 0 && `${countdown.days}d `}
                        {countdown.hours}h {countdown.minutes}m remaining
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
                    Appointments are booked on a first-come, first-served basis. Please check back at the time above to select your slot.
                  </p>
                </div>
              )}

              {mode === 'picker' && bookingOpen && !selfServiceOk && (
                <MoveOutSelfServiceUnavailablePanel />
              )}

              {mode === 'picker' && bookingOpen && selfServiceOk && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Select a one-hour time slot before the end of your lease. Use the arrows to browse earlier weeks. All times are in Eastern Time.
                  </p>
                  <WeekCalendar
                    dates={dateWindow}
                    slotsByDate={slotsByDate}
                    loading={calendarLoading}
                    todayET={todayET}
                    booking={booking}
                    onBook={handleBook}
                    onPrevWeek={() => setWeekOffset((o) => o - 1)}
                    onNextWeek={() => setWeekOffset((o) => Math.min(o + 1, 0))}
                    canGoForward={canGoForward}
                  />
                </div>
              )}
            </div>
            </CollapsibleSection>
          </section>
        )}

        {/* Section: Preparing for Your Move-Out Appointment */}
        <section id="expectations">
          <CollapsibleSection number={2} title="Moving Out of Your Home" isComplete={!!getTask('unit-cleaned')?.completed} forceToggle={sectionToggle} sectionId="expectations" forceOpenId={forceOpenId}>

          {/* Intro */}
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">How do I have a successful move-out?</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">A successful hand-back comes down to three things:</p>
            </div>

            {/* Three pillars */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-4 flex flex-col items-center text-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                  <Sparkles size={20} className="text-red-700 dark:text-red-400" />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Broom Swept Condition</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">The unit must be returned in the condition it was received — clean enough for the next tenants to move in immediately.</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-4 flex flex-col items-center text-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                  <BoxesIcon size={20} className="text-red-700 dark:text-red-400" />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Possessions Removed</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">All tenant belongings must be completely removed from the unit, including basements and storage areas.</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-4 flex flex-col items-center text-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                  <Trash2 size={20} className="text-red-700 dark:text-red-400" />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Garbage Disposed</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">All garbage must be properly disposed of before your appointment. Nothing may be left curbside or in the unit.</p>
              </div>
            </div>
          </div>

          {/* Expectations video */}
          <AutoPlayVideoPlayer items={getContentForSection('expectations-video')} placeholderLabel="Move-Out Expectations Video" overlayTitle="Expectations for Your Move-Out Appointment" />

          {/* Move Out Expectations Examples */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Move Out Expectations Examples</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Below are short video examples showing what each area of your unit should look like before your move-out inspection. Use these as a visual reference alongside the cleaning checklist.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              {
                label: 'Floors & Surfaces — Broom Swept Condition',
                section: 'broom-swept',
                clips: [
                  { color: 'green', desc: 'Panning shot of a clean & tidy living room and kitchen' },
                  { color: 'red', desc: 'Cluttered living room with "INCORRECT" overlaid' },
                ],
              },
              {
                label: 'Removal of Personal Possessions',
                section: 'removal-of-personal-possessions',
                clips: [
                  { color: 'green', desc: 'Empty cabinets, bedrooms, closets, bathroom vanities & showers — all clear' },
                  { color: 'red', desc: 'Rooms with a few items left behind — living room, pantry' },
                ],
              },
              {
                label: 'Fridge Cleaned',
                section: 'fridge-cleaned',
                clips: [
                  { color: 'green', desc: 'Fridge open — all items removed, shelves & drawers cleaned' },
                  { color: 'red', desc: 'Fridge with tenant items remaining & visible spills' },
                ],
              },
              {
                label: 'Oven & Stovetop',
                section: 'oven-stovetop',
                clips: [
                  { color: 'green', desc: 'Clean stovetop surface & reasonably clean oven interior' },
                  { color: 'red', desc: 'Very dirty stovetop & oven caked with built-up grime' },
                ],
              },
              {
                label: 'Microwave',
                section: 'microwave',
                clips: [
                  { color: 'green', desc: 'Empty & clean microwave interior' },
                  { color: 'red', desc: 'Tenant items left inside & dirty interior' },
                ],
              },
              {
                label: 'Kitchen Counters',
                section: 'kitchen-counters',
                clips: [
                  { color: 'green', desc: 'No possessions on counters & surfaces lightly wiped' },
                  { color: 'red', desc: 'Tenant possessions remaining & visible spills' },
                ],
              },
              {
                label: 'Bathroom Surfaces Sanitized & Wiped',
                section: 'bathroom-surfaces',
                clips: [
                  { color: 'green', desc: 'Toilet bowl, counter wiped down & inside of cabinets clean' },
                  { color: 'red', desc: 'Generally messy & unsanitary bathroom condition' },
                ],
              },
              {
                label: 'Wall Decor Removal',
                section: 'wall-decor-removal',
                clips: [
                  { color: 'green', desc: 'Gently removing sticky tack & taking a poster off cleanly' },
                  { color: 'red', desc: 'Ripping poster off, leaving sticky tack & torn paper on the wall' },
                ],
              },
            ].map((card) => {
              const items = getContentForSection(card.section);
              const headerEl = (
                <div className="px-3 py-2.5 border-b border-gray-100 dark:border-white/5 min-h-[3.25rem] flex items-center">
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{card.label}</span>
                </div>
              );
              if (items.length > 0) {
                return (
                  <div key={card.section} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden flex flex-col">
                    {headerEl}
                    <InlineVideo src={items[0].cloudfrontUrl} className="w-full aspect-[9/16] object-cover bg-black" />
                  </div>
                );
              }
              return (
                <div key={card.section} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden flex flex-col">
                  {headerEl}
                  <div className="aspect-[9/16] bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center gap-2 px-4">
                    <Play size={28} className="text-gray-300" />
                    <div className="flex flex-col gap-1.5 mt-1">
                      {card.clips.map((clip, ci) => (
                        <div key={ci} className="flex items-start gap-2">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider w-10 shrink-0 ${clip.color === 'green' ? 'text-green-500' : 'text-red-400'}`}>
                            Clip {ci + 1}
                          </span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-snug">{clip.desc}</span>
                        </div>
                      ))}
                    </div>
                    <span className="text-[9px] text-gray-300 mt-1 italic">Loops continuously</span>
                  </div>
                </div>
              );
            })}

          </div>

          {/* Cleaning Checklist — interactive, DB-backed sub-items */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-white/10">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Cleaning Checklist</h4>
                <span className={`text-xs font-semibold ${allCleaningSubsDone ? 'text-green-600' : 'text-gray-500'}`}>{cleaningSubsDone}/{cleaningSubsTotal}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All required items must be completed before you can confirm your unit condition.</p>
              <div className="mt-2.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${allCleaningSubsDone ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${cleaningSubsTotal ? (cleaningSubsDone / cleaningSubsTotal) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Required section */}
            <div className="px-4 pt-3 pb-1 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5">
              <span className="text-[10px] font-semibold text-red-700 dark:text-red-400 uppercase tracking-wider">Required</span>
            </div>
            {checklistLoading ? (
              <div className="px-5 py-8 flex items-center justify-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                <svg className="animate-spin h-4 w-4 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Loading checklist…
              </div>
            ) : (<>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
              {(['Kitchen', 'Bathroom', 'Unit'] as const).map((cat) => {
                const icon = cat === 'Kitchen' ? <UtensilsCrossed size={14} className="text-red-700 dark:text-red-400" /> : cat === 'Bathroom' ? <Bath size={14} className="text-red-700 dark:text-red-400" /> : <Home size={14} className="text-red-700 dark:text-red-400" />;
                const heading = cat === 'Unit' ? 'The Unit' : cat === 'Bathroom' ? 'Bathrooms' : cat;
                const catRequired = mergedCleaningSubs.filter((s) => s.category === cat && s.tier === 'required');
                const catReqDone = catRequired.filter((s) => s.completed).length;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                      {icon}
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{heading}</span>
                      <span className={`ml-auto text-[10px] font-semibold ${catReqDone === catRequired.length ? 'text-green-600' : 'text-gray-400'}`}>{catReqDone}/{catRequired.length}</span>
                    </div>
                    <ul className="px-4 py-3 space-y-1">
                      {catRequired.map((sub) => {
                        const meta = { type: 'CLEANING_SUB', label: sub.label, category: sub.category, sortOrder: sub.sortOrder };
                        return (
                        <li
                          key={sub.itemKey}
                          role="checkbox"
                          aria-checked={sub.completed}
                          tabIndex={0}
                          onClick={() => handleToggleSimpleTask(sub.itemKey, meta)}
                          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleToggleSimpleTask(sub.itemKey, meta); } }}
                          className="flex items-start gap-2 cursor-pointer group py-1"
                        >
                          <div className={`mt-0.5 w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                            sub.completed
                              ? 'bg-green-600 border border-green-600 text-white'
                              : 'border border-gray-300 group-hover:border-gray-400'
                          }`}>
                            {sub.completed && <Check size={10} strokeWidth={3} />}
                          </div>
                          <span className={`text-xs leading-snug ${sub.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{sub.label}</span>
                        </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-2 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 flex items-center justify-between">
              <button
                onClick={handleExportChecklistPdf}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <FileDown size={12} />
                Export to PDF
              </button>
              <button
                onClick={handleCopyChecklist}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                {requiredCopied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                {requiredCopied ? 'Copied!' : 'Copy checklist'}
              </button>
            </div>
            </>)}
          </div>

          {/* Unit cleaned confirmation CTA — gated behind all cleaning sub-items */}
          {(() => {
            const cleanTask = getTask('unit-cleaned');
            if (!cleanTask) return null;
            const locked = !allCleaningSubsDone && !cleanTask.completed;
            return (
              <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                cleanTask.completed
                  ? 'bg-green-50 border border-green-200'
                  : locked
                    ? 'bg-gray-50 border border-gray-200'
                    : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    cleanTask.completed ? 'bg-green-600' : locked ? 'bg-gray-400' : 'bg-red-600'
                  }`}>
                    {cleanTask.completed
                      ? <Check size={14} className="text-white" strokeWidth={3} />
                      : <Sparkles size={14} className="text-white" />
                    }
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${
                      cleanTask.completed ? 'text-green-600' : locked ? 'text-gray-400' : 'text-red-500'
                    }`}>Task {cleanTask.sortOrder + 1}</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${
                      cleanTask.completed ? 'text-green-900' : locked ? 'text-gray-500' : 'text-red-900'
                    }`}>Confirm Unit Condition</h4>
                  </div>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {cleanTask.completed
                    ? 'Thank you for confirming your unit is ready. If anything changes, you can update this at any time before your appointment.'
                    : locked
                      ? `Complete all required items on the cleaning checklist below before confirming your unit condition (${cleaningSubsDone}/${cleaningSubsTotal} required items completed).`
                      : 'All required cleaning items are complete. Confirm that your unit has been broom swept, all personal possessions have been removed, and all garbage has been properly disposed of.'}
                </p>

                <div
                  role="checkbox"
                  aria-checked={cleanTask.completed}
                  aria-disabled={locked}
                  tabIndex={locked ? -1 : 0}
                  onClick={() => { if (!locked) handleToggleSimpleTask('unit-cleaned'); }}
                  onKeyDown={(e) => { if (!locked && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); handleToggleSimpleTask('unit-cleaned'); } }}
                  className={`flex items-center gap-3 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    cleanTask.completed
                      ? 'bg-green-600 border-green-600 text-white'
                      : locked
                        ? 'border-gray-300'
                        : 'border-red-400 group-hover:border-red-500'
                  }`}>
                    {cleanTask.completed && <Check size={13} strokeWidth={3} />}
                  </div>
                  <span className={`text-sm leading-snug ${cleanTask.completed ? 'text-green-800 line-through' : locked ? 'text-gray-400' : 'text-gray-800'}`}>
                    {cleanTask.label}
                  </span>
                </div>
              </div>
            );
          })()}

          </CollapsibleSection>
        </section>

        {/* Section: Garbage & Furniture Disposal */}
        <section id="garbage-disposal">
          <CollapsibleSection number={3} title="Garbage & Furniture Disposal" isComplete={!!getTask('disposal-plan-confirmed')?.completed} forceToggle={sectionToggle} sectionId="garbage-disposal" forceOpenId={forceOpenId}>

          {/* Disposal video */}
          <AutoPlayVideoPlayer items={getContentForSection('garbage-disposal-video')} placeholderLabel="Garbage & Disposal Guide Video" overlayTitle="How to Dispose of Your Garbage and Furniture" loading={contentLoading} />

          {/* Intro */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Getting Rid of Your Junk!</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              From our extensive experience with move-outs, there are two things you need to know:
            </p>
            <ul className="space-y-1.5 pl-1">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300"><span className="font-medium text-gray-900 dark:text-white">You will have more garbage than you expect.</span></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300"><span className="font-medium text-gray-900 dark:text-white">Disposing of it all will take more time and effort than you think.</span></span>
              </li>
            </ul>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              It is important to plan early, especially since you may be packing and moving during final exams or other busy periods.
            </p>

            {/* Garbage disposal limitations — sub-card */}
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-lg p-4 space-y-2.5">
              <h4 className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Garbage Disposal Limitations</h4>
              <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
                The City of Kingston has strict rules about curbside garbage collection.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-3.5 h-3.5 rounded bg-amber-200 border border-amber-400 flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-amber-700 dark:text-amber-400">1</span>
                  <span className="text-sm text-amber-800 dark:text-amber-300">You are permitted <span className="font-semibold">one untagged bag per unit per week</span> for curbside collection. Every additional bag requires a garbage tag, available at most grocery or convenience stores.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-3.5 h-3.5 rounded bg-amber-200 border border-amber-400 flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-amber-700 dark:text-amber-400">2</span>
                  <span className="text-sm text-amber-800 dark:text-amber-300">You may <span className="font-semibold">not</span> leave garbage bags on the curb for future pickup after you vacate.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-3.5 h-3.5 rounded bg-amber-200 border border-amber-400 flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-amber-700 dark:text-amber-400">3</span>
                  <span className="text-sm text-amber-800 dark:text-amber-300">Our team will <span className="font-semibold">not</span> put out bins or bags for upcoming garbage or recycling days on your behalf.</span>
                </li>
              </ul>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed pt-0.5">
                Given these limitations, most tenants find that arranging a disposal service or renting a bin is the most practical option.
              </p>
            </div>
          </div>

          {/* Disposal options */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-white/10">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Disposal Options</h4>
            </div>

            <div className="divide-y divide-gray-100">
              {/* Option 1: Bin rental */}
              <div className="px-5 py-4 space-y-3">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white">1. Rent a Disposal Bin</h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  We highly recommend arranging for garbage collection services. You can arrange for a day where the bin is dropped off and picked up, so all you need to worry about is throwing items into the bin.
                </p>

                {/* Bin size visualizer */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Common Bin Sizes</p>
                  <div className="grid grid-cols-5 gap-2 items-end">
                    {[
                      { yard: 6,  l: 12, w: 6,   h: 1.5, trucks: '2–3',   bags: 33,  mattresses: '3–4',  couches: '1',    size: 'XS', color: 'emerald' },
                      { yard: 10, l: 12, w: 5.5, h: 4,   trucks: '4–5',   bags: 55,  mattresses: '6–8',  couches: '2',    size: 'S',  color: 'lime' },
                      { yard: 14, l: 12, w: 7,   h: 4.5, trucks: '6–7',   bags: 77,  mattresses: '10–12', couches: '3',    size: 'M',  color: 'amber' },
                      { yard: 20, l: 12, w: 7,   h: 6,   trucks: '8–10',  bags: 110, mattresses: '15–18', couches: '4–5',  size: 'L',  color: 'orange' },
                      { yard: 40, l: 20, w: 8,   h: 7,   trucks: '16–18', bags: 220, mattresses: '35–40', couches: '10–12', size: 'XL', color: 'red' },
                    ].map((bin) => {
                      const colorMap: Record<string, { bg: string; border: string; badge: string; fill: string; text: string }> = {
                        emerald: { bg: 'bg-emerald-50/60', border: 'border-emerald-200', badge: 'bg-emerald-600', fill: 'bg-emerald-400', text: 'text-emerald-700' },
                        lime:    { bg: 'bg-lime-50/60',    border: 'border-lime-200',    badge: 'bg-lime-600',    fill: 'bg-lime-400',    text: 'text-lime-700' },
                        amber:   { bg: 'bg-amber-50/60',   border: 'border-amber-200',   badge: 'bg-amber-600',  fill: 'bg-amber-400',   text: 'text-amber-700' },
                        orange:  { bg: 'bg-orange-50/60',  border: 'border-orange-200',  badge: 'bg-orange-600', fill: 'bg-orange-400',  text: 'text-orange-700' },
                        red:     { bg: 'bg-red-50/60',     border: 'border-red-200',     badge: 'bg-red-600',    fill: 'bg-red-400',     text: 'text-red-700' },
                      };
                      const c = colorMap[bin.color];
                      const maxBags = 220;
                      const barPct = Math.round((bin.bags / maxBags) * 100);
                      return (
                        <div key={bin.yard} className="flex flex-col items-center gap-1.5">
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${c.badge}`}>{bin.size}</span>
                          <div className={`w-full flex flex-col items-center gap-1.5 p-2.5 rounded-xl border ${c.border} ${c.bg} text-center`}>
                            <div className="w-8 rounded-md bg-gray-200/80 overflow-hidden flex flex-col justify-end" style={{ height: 56 }}>
                              <div className={`w-full rounded-md ${c.fill} transition-all`} style={{ height: `${barPct}%` }} />
                            </div>
                            <span className={`text-lg font-extrabold leading-none ${c.text}`}>{bin.yard}</span>
                            <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Yard</span>
                            <span className="text-[9px] text-gray-400 dark:text-gray-500 leading-snug">{bin.l}′L × {bin.w}′W × {bin.h}′H</span>
                            <span className={`text-[10px] font-bold ${c.text}`}>~{bin.bags} bags</span>
                            <span className={`text-[10px] font-bold ${c.text}`}>~{bin.trucks} truck loads</span>
                            <span className={`text-[10px] font-bold ${c.text}`}>~{bin.mattresses} mattresses</span>
                            <span className={`text-[10px] font-bold ${c.text}`}>~{bin.couches} couches</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">Bag estimates based on large 33-gallon bags. Dimensions and truck loads sourced from First Capital Disposal.</p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  We often use the following companies and have had very positive experiences with them:
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10">
                    <a href="https://quote.bintheredumpthat.com/kingston-dumpster-rentals" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 group">
                      <div className="h-10 flex items-center justify-center">
                        <img src="/images/vendors/bin_there_dump_that.webp" alt="Bin There Dump That" className="max-h-10 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }} />
                        <span className="hidden text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-red-700">BTDT</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-red-700">Bin There, Dump That</span>
                    </a>
                    <a href="tel:+16137038857" className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-400 transition-colors">(613) 703-8857</a>
                  </div>
                  <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10">
                    <a href="https://firstcapitaldisposal.ca" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 group">
                      <div className="h-10 flex items-center justify-center">
                        <img src="/images/vendors/first_capital_disposal.png" alt="First Capital Disposal" className="max-h-10 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }} />
                        <span className="hidden text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-red-700">FCD</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-red-700">First Capital Disposal</span>
                    </a>
                    <a href="tel:+16135302468" className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-400 transition-colors">(613) 530-2468</a>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-3 mt-1">
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    <span className="font-semibold">Pro tip:</span> Tenants consistently underestimate how much stuff they've accumulated. A single bin could save you 10 round trip drives to the dump. Each trip takes about an hour. With a rental bin, you can toss furniture, bags, and bulky items all in one place without worrying about bag limits, tags, or scheduling.
                  </p>
                </div>
              </div>

              {/* Option 2: Amber Peak services */}
              <div className="px-5 py-4 space-y-1.5">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white">2. Amber Peak Disposal Services</h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Our team is able to provide garbage removal services in the weeks leading up to your lease end date. We can be a more effective provider when you have a smaller quantity of items.
                </p>
                <div className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden mt-1">
                  <div className="hidden sm:grid grid-cols-[1fr_100px_140px] bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                    <span className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Service</span>
                    <span className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide text-right">Price</span>
                    <span className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide text-center">Request</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {/* Furniture removal row */}
                    <div>
                      <div className="flex flex-col sm:grid sm:grid-cols-[1fr_100px_140px] sm:items-center">
                        <div className="px-3 py-2.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Large furniture removal</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Couches, bed frames, dining tables, mattresses</p>
                        </div>
                        <div className="px-3 pb-1 sm:py-2.5 sm:text-right">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">$100</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">+HST</span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">per item</span>
                        </div>
                        <div className="px-3 pb-2.5 sm:py-2.5 flex items-center sm:justify-center">
                          {serviceRequested.has('furniture-removal') ? (
                            <div className="flex flex-col items-center sm:items-end gap-0.5">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                                <CheckCircle2 size={14} /> Sent
                              </span>
                              {serviceWorkOrderRefs['furniture-removal'] && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Ref #{serviceWorkOrderRefs['furniture-removal']}</span>
                              )}
                            </div>
                          ) : servicePickingDate === 'furniture-removal' ? null : (
                            <button
                              onClick={() => { setServicePickingDate('furniture-removal'); setServiceDate(''); }}
                              className="text-xs font-medium px-4 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800 transition-colors text-center"
                            >
                              Request
                            </button>
                          )}
                        </div>
                      </div>
                      {servicePickingDate === 'furniture-removal' && !serviceRequested.has('furniture-removal') && (
                        <div className="mx-3 mb-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">Select your preferred date for furniture removal</p>
                          <div className="border-t border-gray-200 dark:border-white/10 pt-3 flex justify-center">
                            <ServiceCalendar
                              selected={serviceDate}
                              onSelect={setServiceDate}
                              minDate={getEasternToday()}
                              maxDate={moveOut?.appointmentStartTime ? new Date(moveOut.appointmentStartTime).toISOString().slice(0, 10) : moveOut?.leaseEndDate ?? '2099-12-31'}
                              leaseEndDate={moveOut?.leaseEndDate ?? null}
                              appointmentDate={moveOut?.appointmentStartTime ?? null}
                            />
                          </div>
                          <div className="border-t border-gray-200 dark:border-white/10 pt-3 flex items-center gap-2">
                            {serviceDate && (
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                Selected: <span className="font-semibold">{serviceDate}</span>
                              </span>
                            )}
                            <div className="flex-1" />
                            <button
                              onClick={() => setServicePickingDate(null)}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleRequestService('furniture-removal', serviceDate)}
                              disabled={!serviceDate || serviceRequesting === 'furniture-removal'}
                              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800 transition-colors disabled:opacity-50"
                            >
                              {serviceRequesting === 'furniture-removal' ? (
                                <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Sending...</span>
                              ) : 'Submit Request'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Garbage pickup row */}
                    <div>
                      <div className="flex flex-col sm:grid sm:grid-cols-[1fr_100px_140px] sm:items-center">
                        <div className="px-3 py-2.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Bagged garbage pickup</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Regular or large garbage bags</p>
                        </div>
                        <div className="px-3 pb-1 sm:py-2.5 sm:text-right">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">$25</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">+HST</span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">per 5 bags</span>
                        </div>
                        <div className="px-3 pb-2.5 sm:py-2.5 flex items-center sm:justify-center">
                          {serviceRequested.has('garbage-pickup') ? (
                            <div className="flex flex-col items-center sm:items-end gap-0.5">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                                <CheckCircle2 size={14} /> Sent
                              </span>
                              {serviceWorkOrderRefs['garbage-pickup'] && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Ref #{serviceWorkOrderRefs['garbage-pickup']}</span>
                              )}
                            </div>
                          ) : servicePickingDate === 'garbage-pickup' ? null : (
                            <button
                              onClick={() => { setServicePickingDate('garbage-pickup'); setServiceDate(''); }}
                              className="text-xs font-medium px-4 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800 transition-colors text-center"
                            >
                              Request
                            </button>
                          )}
                        </div>
                      </div>
                      {servicePickingDate === 'garbage-pickup' && !serviceRequested.has('garbage-pickup') && (
                        <div className="mx-3 mb-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">Select your preferred date for garbage pickup</p>
                          <div className="border-t border-gray-200 dark:border-white/10 pt-3 flex justify-center">
                            <ServiceCalendar
                              selected={serviceDate}
                              onSelect={setServiceDate}
                              minDate={getEasternToday()}
                              maxDate={moveOut?.appointmentStartTime ? new Date(moveOut.appointmentStartTime).toISOString().slice(0, 10) : moveOut?.leaseEndDate ?? '2099-12-31'}
                              leaseEndDate={moveOut?.leaseEndDate ?? null}
                              appointmentDate={moveOut?.appointmentStartTime ?? null}
                            />
                          </div>
                          <div className="border-t border-gray-200 dark:border-white/10 pt-3 flex items-center gap-2">
                            {serviceDate && (
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                Selected: <span className="font-semibold">{serviceDate}</span>
                              </span>
                            )}
                            <div className="flex-1" />
                            <button
                              onClick={() => setServicePickingDate(null)}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleRequestService('garbage-pickup', serviceDate)}
                              disabled={!serviceDate || serviceRequesting === 'garbage-pickup'}
                              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800 transition-colors disabled:opacity-50"
                            >
                              {serviceRequesting === 'garbage-pickup' ? (
                                <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Sending...</span>
                              ) : 'Submit Request'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {disposalWorkOrders.length > 0 && (
                  <div className="mt-3 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10 px-3 py-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Your Disposal Requests</span>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Ref #</th>
                          <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Service</th>
                          <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Status</th>
                          <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Scheduled</th>
                          <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Requested</th>
                          <th className="text-right px-3 py-1.5 border-b border-gray-200 dark:border-white/10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {disposalWorkOrders.map((wo) => {
                          const isFurniture = wo.title?.includes('[DISPOSAL:FURNITURE]');
                          const serviceLabel = isFurniture ? 'Furniture Removal' : 'Garbage Pickup';
                          const appt = wo.appointments?.[0];
                          const scheduledDate = appt?.startTime
                            ? new Date(appt.startTime).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })
                            : null;
                          const scheduledTime = appt?.startTime
                            ? new Date(appt.startTime).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })
                            : null;
                          const requestedDate = new Date(wo.createdAt).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });

                          const statusLabel = wo.status === 'NEW' ? 'New' : wo.status === 'IN_PROGRESS' ? 'In Progress' : wo.status === 'COMPLETED' ? 'Done' : wo.status ?? '—';
                          const statusColor = wo.status === 'COMPLETED' ? 'text-green-700 bg-green-50' : wo.status === 'IN_PROGRESS' ? 'text-blue-700 bg-blue-50' : 'text-gray-600 bg-gray-100';
                          const isConfirming = disposalDeleteConfirm === wo.id;
                          const isDeleting = disposalDeleting === wo.id;

                          return (
                            <tr key={wo.id} className={isConfirming ? 'bg-red-50/40' : ''}>
                              <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">
                                {wo.referenceNumber ? `#${wo.referenceNumber}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1.5">
                                  {isFurniture ? '🛋️' : '🗑️'} {serviceLabel}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColor}`}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                {scheduledDate ? (
                                  <span>{scheduledDate}{scheduledTime && <span className="text-gray-400 dark:text-gray-500 ml-1">{scheduledTime}</span>}</span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500">Pending</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{requestedDate}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {isConfirming ? (
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">Delete?</span>
                                    <button
                                      onClick={() => handleDeleteDisposalWO(wo.id)}
                                      disabled={isDeleting}
                                      className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                                    >
                                      {isDeleting ? <Loader2 size={10} className="animate-spin" /> : 'Yes'}
                                    </button>
                                    <button
                                      onClick={() => setDisposalDeleteConfirm(null)}
                                      disabled={isDeleting}
                                      className="text-[10px] font-medium px-2 py-0.5 rounded border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-100 transition-colors disabled:opacity-50"
                                    >
                                      No
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setDisposalDeleteConfirm(wo.id)}
                                    className="text-[10px] font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Option 3: Self-disposal */}
              <div className="px-5 py-4 space-y-3">
                <h5 className="text-sm font-semibold text-gray-900 dark:text-white">3. Self-Disposal</h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  You can take garbage directly to the dump or tag additional garbage bags for curbside collection the week prior to your move-out date.
                </p>

                {/* Waste Connections of Canada */}
                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3">
                  <div className="flex flex-col items-center text-center gap-1.5 pb-2 border-b border-gray-200 dark:border-white/10">
                    <h6 className="text-base font-bold text-gray-900 dark:text-white">Kingston Self-Serve Landfill</h6>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Waste Connections of Canada</span>
                    <img src="/images/vendors/Waste-Connections.png" alt="Waste Connections of Canada" className="h-10 object-contain mt-1" />
                  </div>

                  {/* Contact details — proportional grid */}
                  <div className="grid gap-3" style={{ gridTemplateColumns: '2fr 1fr 2fr' }}>
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</span>
                      <a
                        href="https://maps.google.com/?q=1266+McAdoo's+Ln+RR%231,+Glenburnie,+ON+K0H+1S0"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors leading-snug"
                      >
                        1266 McAdoo's Ln RR#1, Glenburnie, ON K0H 1S0
                      </a>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</span>
                      <a href="tel:+16135484428" className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors">
                        (613) 548-4428
                      </a>
                    </div>
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Website</span>
                      <a
                        href="https://www.wasteconnectionscanada.com/kingston"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors"
                      >
                        wasteconnectionscanada.com <ExternalLink size={10} />
                      </a>
                    </div>
                  </div>

                  {/* Hours table with contextual date labels */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Schedule & Your Dates</span>
                    {(() => {
                      const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const HOURS_MAP: Record<string, string> = {
                        Monday: '7:00 AM – 4:30 PM',
                        Tuesday: '7:00 AM – 4:30 PM',
                        Wednesday: '7:00 AM – 4:30 PM',
                        Thursday: '7:00 AM – 4:30 PM',
                        Friday: '7:00 AM – 4:30 PM',
                        Saturday: 'Closed',
                        Sunday: 'Closed',
                      };

                      const leaseEnd = moveOut?.leaseEndDate ?? null;
                      const apptStart = moveOut?.appointmentStartTime ?? null;

                      const leaseEndDate = leaseEnd ? new Date(leaseEnd + 'T12:00:00') : null;
                      const leaseEndPrevDate = leaseEnd ? (() => {
                        const d = new Date(leaseEnd + 'T12:00:00');
                        d.setDate(d.getDate() - 1);
                        return d;
                      })() : null;
                      const apptDate = apptStart ? new Date(apptStart) : null;

                      const leaseEndDay = leaseEndDate ? DAYS[leaseEndDate.getDay()] : null;
                      const leaseEndPrevDay = leaseEndPrevDate ? DAYS[leaseEndPrevDate.getDay()] : null;
                      const apptDay = apptDate ? DAYS[apptDate.getDay()] : null;

                      const fmtDate = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

                      type TagInfo = { label: string; date: string; color: string };

                      const ordered = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

                      const hasAppointment = !!apptDate;

                      const rows = ordered.map((day) => {
                        const isClosed = HOURS_MAP[day] === 'Closed';
                        const tags: TagInfo[] = [];
                        if (hasAppointment) {
                          if (day === apptDay && apptDate) tags.push({ label: 'Your Move Out Appointment', date: fmtDate(apptDate), color: 'bg-green-100 text-green-700' });
                        } else {
                          if (day === leaseEndPrevDay && leaseEndPrevDate) tags.push({ label: 'Day before lease ends', date: fmtDate(leaseEndPrevDate), color: 'bg-gray-100 text-gray-600' });
                          if (day === leaseEndDay && leaseEndDate) tags.push({ label: 'Last day of lease', date: fmtDate(leaseEndDate), color: 'bg-red-100 text-red-700' });
                        }
                        return { day, hours: HOURS_MAP[day], isClosed, tags };
                      });

                      return (
                        <div className="overflow-x-auto border border-gray-200 dark:border-white/10 rounded-lg">
                          <table className="w-full text-xs" style={{ minWidth: 360 }}>
                            <thead>
                              <tr className="bg-gray-100 dark:bg-gray-800 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Day</th>
                                <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Hours</th>
                                <th className="text-left px-3 py-1.5 border-b border-gray-200 dark:border-white/10">Your Dates</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {rows.map((row) => (
                                <tr key={row.day} className={row.tags.length > 0 ? 'bg-gray-50/50' : ''}>
                                  <td className={`px-3 py-2 font-medium whitespace-nowrap ${row.isClosed ? 'text-gray-400' : 'text-gray-800'}`}>
                                    {row.day}
                                  </td>
                                  <td className={`px-3 py-2 ${row.isClosed ? 'text-gray-400' : 'text-gray-700'}`}>
                                    {row.isClosed ? row.hours : (
                                      <>
                                        <span className="whitespace-nowrap">7:00 AM</span>
                                        <span className="whitespace-nowrap"> – 4:30 PM</span>
                                      </>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.tags.length > 0 ? (
                                      <div className="flex flex-col gap-1">
                                        {row.tags.map((tag) => (
                                          <div key={tag.label} className="flex items-center gap-1.5 flex-wrap">
                                            <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${tag.color}`}>
                                              {tag.label}
                                            </span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">{tag.date}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Directions map */}
                  {tenant?.residentialLease?.address && import.meta.env.VITE_GOOGLE_MAPS_API_KEY && (
                    <DirectionsMap origin={
                      tenant.residentialLease.address.toLowerCase().includes('100 test')
                        ? '459 Princess St, Kingston, ON K7L 1C3'
                        : tenant.residentialLease.address
                    } />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Food donation section */}
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-xl overflow-hidden">
            <button
              onClick={() => setFoodBankOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-green-100/50 transition-colors text-left"
            >
              <h4 className="text-sm font-semibold text-green-900">Do you have food or household supplies you would like to donate?</h4>
              <ChevronDown size={16} className={`text-green-600 shrink-0 ml-3 transition-transform duration-200 ${foodBankOpen ? 'rotate-180' : ''}`} />
            </button>

            {foodBankOpen && <div className="border-t border-green-200 dark:border-green-900/40">
              <div className="p-5 space-y-2">
                <p className="text-sm text-green-800 dark:text-green-300 leading-relaxed">
                  Rather than tossing out unopened food, cleaning supplies, or household items, consider dropping them off at a local charity. It takes just a few minutes and makes a real difference for families in Kingston.
                </p>
                <p className="text-sm text-green-800 dark:text-green-300 leading-relaxed">
                  Anything left behind in the unit will be treated as garbage and removed at your expense. Please double-check cupboards and underneath sinks before you leave.
                </p>
              </div>

              <div className="border-t border-green-200 dark:border-green-900/40 px-5 pb-5 pt-4">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-3">Local Organizations Accepting Donations</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Partners in Mission Food Bank */}
                <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-900/40 rounded-lg p-4 flex flex-col">
                  <div className="flex flex-col items-center text-center gap-1.5 pb-2 border-b border-green-100 h-28 justify-center">
                    <img src="/images/vendors/partners-in-mission.png" alt="Partners in Mission Food Bank" className="h-10 object-contain" />
                    <a
                      href="https://www.kingstonfoodbank.ca/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-green-900 hover:text-green-700 transition-colors"
                    >
                      Partners in Mission Food Bank
                    </a>
                    <p className="text-[10px] text-green-700 dark:text-green-400 line-clamp-2">Non-perishable food, fresh foods, milk, eggs, fruits &amp; vegetables</p>
                  </div>

                  <div className="flex-1 space-y-3 pt-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</span>
                      <a
                        href="https://maps.google.com/?q=4+Harvey+Street,+Kingston,+ON+K7K+5B9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors"
                      >
                        4 Harvey Street, Kingston, ON K7K 5B9
                      </a>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</span>
                      <a href="tel:+16135444100" className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors">
                        (613) 544-4100
                      </a>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Hours</span>
                      <div className="grid grid-cols-[80px_1fr] text-xs text-gray-700 dark:text-gray-300 gap-y-0.5">
                        <span className="font-medium">Monday</span><span>8:30 AM – 4:30 PM</span>
                        <span className="font-medium">Tuesday</span><span>8:30 AM – 4:30 PM</span>
                        <span className="font-medium">Wednesday</span><span>8:30 AM – 4:30 PM</span>
                        <span className="font-medium">Thursday</span><span>8:30 AM – 4:30 PM</span>
                        <span className="font-medium">Friday</span><span>8:30 AM – 4:00 PM</span>
                        <span className="font-medium text-gray-400 dark:text-gray-500">Saturday</span><span className="text-gray-400 dark:text-gray-500">Closed</span>
                        <span className="font-medium text-gray-400 dark:text-gray-500">Sunday</span><span className="text-gray-400 dark:text-gray-500">Closed</span>
                      </div>
                    </div>
                  </div>

                  <a
                    href="https://www.kingstonfoodbank.ca/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors mt-3"
                  >
                    <ExternalLink size={12} />
                    Visit Website
                  </a>
                </div>

                {/* Martha's Table */}
                <div className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-900/40 rounded-lg p-4 flex flex-col">
                  <div className="flex flex-col items-center text-center gap-1.5 pb-2 border-b border-green-100 h-28 justify-center">
                    <img src="/images/vendors/Marthas-Table_Logo.png" alt="Martha's Table" className="h-10 object-contain" />
                    <a
                      href="https://marthastable.ca/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-green-900 hover:text-green-700 transition-colors"
                    >
                      Martha's Table
                    </a>
                    <p className="text-[10px] text-green-700 dark:text-green-400 line-clamp-2">Prepared meals, non-perishable food, toiletries, utensils</p>
                  </div>

                  <div className="flex-1 space-y-3 pt-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</span>
                      <a
                        href="https://maps.google.com/?q=629+Princess+St,+Kingston,+ON+K7L+1E2"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors"
                      >
                        629 Princess St, Kingston, ON K7L 1E2
                      </a>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</span>
                      <a href="tel:+16135460320" className="block text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 underline underline-offset-2 transition-colors">
                        (613) 546-0320
                      </a>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Hours</span>
                      <div className="grid grid-cols-[80px_1fr] text-xs text-gray-700 dark:text-gray-300 gap-y-0.5">
                        <span className="font-medium">Monday</span><span>9:00 AM – 4:00 PM</span>
                        <span className="font-medium">Tuesday</span><span>9:00 AM – 4:00 PM</span>
                        <span className="font-medium">Wednesday</span><span>9:00 AM – 4:00 PM</span>
                        <span className="font-medium">Thursday</span><span>9:00 AM – 4:00 PM</span>
                        <span className="font-medium">Friday</span><span>9:00 AM – 4:00 PM</span>
                        <span className="font-medium text-gray-400 dark:text-gray-500">Saturday</span><span className="text-gray-400 dark:text-gray-500">Closed</span>
                        <span className="font-medium text-gray-400 dark:text-gray-500">Sunday</span><span className="text-gray-400 dark:text-gray-500">Closed</span>
                      </div>
                    </div>
                  </div>

                  <a
                    href="https://marthastable.ca/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors mt-3"
                  >
                    <ExternalLink size={12} />
                    Visit Website
                  </a>
                </div>
              </div>
              </div>
            </div>}
          </div>

          {/* Disposal plan confirmation */}
          {(() => {
            const disposalTask = getTask('disposal-plan-confirmed');
            if (!disposalTask) return null;
            return (
              <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                disposalTask.completed
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    disposalTask.completed ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {disposalTask.completed
                      ? <Check size={14} className="text-white" strokeWidth={3} />
                      : <Trash2 size={14} className="text-white" />
                    }
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${
                      disposalTask.completed ? 'text-green-600' : 'text-red-500'
                    }`}>Task {disposalTask.sortOrder + 1}</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${
                      disposalTask.completed ? 'text-green-900' : 'text-red-900'
                    }`}>Confirm Disposal Plan</h4>
                  </div>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {disposalTask.completed
                    ? 'Thank you for confirming. If your plans change, you can update this at any time.'
                    : 'Whether you\'re renting a bin, using our services, or taking items to the dump yourself — confirm that you\'ve made a plan so nothing is left behind on move-out day.'}
                </p>

                <div
                  role="checkbox"
                  aria-checked={disposalTask.completed}
                  tabIndex={0}
                  onClick={() => handleToggleSimpleTask('disposal-plan-confirmed')}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleToggleSimpleTask('disposal-plan-confirmed'); } }}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    disposalTask.completed
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-red-400 group-hover:border-red-500'
                  }`}>
                    {disposalTask.completed && <Check size={13} strokeWidth={3} />}
                  </div>
                  <span className={`text-sm leading-snug ${disposalTask.completed ? 'text-green-800 line-through' : 'text-gray-800'}`}>
                    {disposalTask.label}
                  </span>
                </div>
              </div>
            );
          })()}

          </CollapsibleSection>
        </section>

        {/* Section: Close Utilities Account */}
        <section id="utilities">
          <CollapsibleSection number={4} title="Close Your Utilities Account" isComplete={!!getTask('utilities-closed')?.completed} forceToggle={sectionToggle} sectionId="utilities" forceOpenId={forceOpenId}>

          {/* Blurb + instructions + video + confirmation */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
            <div className="p-5 space-y-3">
            <div className="flex items-center gap-2.5">
              <Zap size={20} className="text-red-700 dark:text-red-400 shrink-0" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Closing Your Utilities Account</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Your account with Utilities Kingston must be closed for the last day of your lease. Your account must be held until this date, regardless of when your move-out appointment is.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              For example, if you vacate the unit on April 25th, the last day of your account with Utilities Kingston would still be April 30th.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              If the account is closed prior to the last day of your lease, any utility charges incurred until that date will be passed along with administrative fees.
            </p>

            {/* How to close */}
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              Visit the{' '}
              <a href="https://utilitieskingston.com/Accounts/OpenClose/Moving" target="_blank" rel="noopener noreferrer" className="font-medium text-red-700 dark:text-red-400 underline hover:text-red-900 dark:hover:text-red-200 inline-flex items-center gap-1">
                Utilities Kingston website
                <ExternalLink size={12} />
              </a>
              , select "Move Out" as your move type, and set the move-out date to the last day of your lease.
            </p>

            {/* Lease end date copy helper */}
            {leaseEndDate && (() => {
              const [y, m, d] = leaseEndDate.split('-').map(Number);
              const dt = new Date(y, m - 1, d);
              const formatted = dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
              return (
                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-3.5 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Your account closure date</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100 mt-0.5">{formatted}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Use this date when closing your Utilities Kingston account</p>
                  </div>
                  <button
                    onClick={handleCopyLeaseEnd}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors shrink-0"
                    style={leaseEndCopied ? { background: '#dcfce7', borderColor: '#86efac', color: '#166534' } : { background: 'white', borderColor: '#d1d5db', color: '#374151' }}
                  >
                    {leaseEndCopied ? <Check size={13} /> : <Copy size={13} />}
                    {leaseEndCopied ? 'Copied!' : 'Copy date'}
                  </button>
                </div>
              );
            })()}
            </div>

            {/* Utilities how-to video */}
            <AutoPlayVideoPlayer
              items={getContentForSection('utilities-video')}
              placeholderLabel="Cancel Utilities How-To"
              overlayTitle="How to Close Your Utilities Account"
              aspectClass="aspect-[666/720]"
              showSoundButton={false}
              darkControls
            />

            {/* Confirmation CTA */}
            {utilitiesTask && (() => {
              const ut = utilitiesTask;
              const done = utilitiesChecked && !!ut.attachment;
              return (
            <div className={`m-5 rounded-xl p-5 space-y-4 transition-colors ${
              done ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-green-600' : 'bg-red-600'}`}>
                    {done ? <Check size={14} className="text-white" strokeWidth={3} /> : <Upload size={14} className="text-white" />}
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${done ? 'text-green-600' : 'text-red-500'}`}>Task {ut.sortOrder + 1}</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${done ? 'text-green-900' : 'text-red-900'}`}>Confirm Account Closure</h4>
                  </div>
                </div>

                <div
                  role="checkbox"
                  aria-checked={utilitiesChecked}
                  tabIndex={0}
                  onClick={() => handleToggleDualTask('utilities-closed')}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleToggleDualTask('utilities-closed'); } }}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    utilitiesChecked ? 'bg-green-600 border-green-600 text-white' : 'border-red-400 group-hover:border-red-500'
                  }`}>
                    {utilitiesChecked && <Check size={13} strokeWidth={3} />}
                  </div>
                  <span className={`text-sm leading-snug ${done ? 'text-green-800 line-through' : 'text-gray-800'}`}>
                    {ut.label}
                  </span>
                </div>
                {utilitiesChecked && !ut.attachment && (
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium ml-8">Upload your confirmation below to complete this step.</p>
                )}
                {!utilitiesChecked && ut.attachment && (
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium ml-8">Check the box above to complete this step.</p>
                )}

                <div className={`rounded-lg p-4 ${ut.attachment ? 'bg-white border border-green-200' : 'bg-white border-2 border-dashed border-red-300'}`}>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Upload a screenshot of your confirmation as proof of account closure.</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-3">Accepted formats: JPG, PNG, WEBP, HEIC, PDF</p>

                  {utilitiesUploadError && (
                    <div className="flex items-start gap-2 p-2.5 mb-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-lg">
                      <AlertCircle size={14} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-red-700 dark:text-red-400">{utilitiesUploadError}</p>
                    </div>
                  )}

                  {ut.attachment ? (
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-md px-3 py-2.5">
                      <Paperclip size={14} className="text-green-600 shrink-0" />
                      <span className="text-sm text-green-900 font-medium truncate flex-1">{ut.attachment.filename}</span>
                      <a href={resolveMediaUrl(ut.attachment.url) ?? '#'} target="_blank" rel="noopener noreferrer" className="text-xs text-green-700 dark:text-green-400 hover:text-green-900 font-semibold shrink-0">View</a>
                      <span className="text-green-300 shrink-0">|</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(ut)}
                        disabled={deletingAttachmentId === ut.dbId}
                        className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-700 dark:hover:text-red-400 font-medium shrink-0 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        {deletingAttachmentId === ut.dbId ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.heic,.heif,.pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const raw = e.target.files?.[0];
                          e.target.value = '';
                          if (!raw) return;
                          setUtilitiesUploadError(null);
                          const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
                          const isHeic = isHeicFile(raw);
                          if (!allowed.includes(raw.type) && !isHeic) {
                            setUtilitiesUploadError(`"${raw.name}" is not a supported file type. Please upload a JPG, PNG, WEBP, HEIC, or PDF.`);
                            return;
                          }
                          try {
                            let file = raw;
                            if (isHeic) {
                              setUtilitiesConverting(true);
                              try { file = await convertHeicToJpeg(raw); } finally { setUtilitiesConverting(false); }
                            }
                            const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
                            const dateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                            const tenantName = tenant ? `${tenant.firstName} ${tenant.lastName}` : 'Tenant';
                            const renamedFile = new File([file], `${dateStr} - ${tenantName} - Utilities Cancellation Confirmation${ext}`, { type: file.type });
                            await handleUploadAttachment(ut, renamedFile);
                          } catch {
                            setUtilitiesUploadError(isHeic ? 'Failed to convert HEIC photo. Please try converting it to JPG first, or use a different image.' : 'Upload failed. Please try again.');
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={(uploadingItemId != null && uploadingItemId === ut.dbId) || utilitiesConverting}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm"
                      >
                        {utilitiesConverting ? (<><Loader2 size={14} className="animate-spin" /> Converting photo…</>) : (uploadingItemId != null && uploadingItemId === ut.dbId) ? (<><Loader2 size={14} className="animate-spin" /> Uploading…</>) : (<><Upload size={14} /> Upload Confirmation</>)}
                      </button>
                    </>
                  )}
                </div>
            </div>
              );
            })()}
          </div>
          </CollapsibleSection>
        </section>

        {/* Section: Disconnecting Internet Service */}
        <section id="internet">
          <CollapsibleSection number={5} title="Disconnecting Internet Service" isComplete={!!getTask('internet-disconnected')?.completed && !!getTask('internet-router-returned')?.completed} forceToggle={sectionToggle} sectionId="internet" forceOpenId={forceOpenId}>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-3">
            <div className="flex items-center gap-2.5">
              <Wifi size={20} className="text-red-700 dark:text-red-400 shrink-0" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Disconnecting Your Internet</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              You are responsible for cancelling or transferring your internet service before you vacate the unit. Contact your internet service provider to schedule a disconnection for the last day of your lease.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              If your provider requires equipment to be returned (modem, router, etc.), make sure to arrange for pickup or drop-off before your move-out appointment.
            </p>

            {/* Provider cancellation guides */}
            <div className="border-t border-gray-200 dark:border-white/10 pt-4 mt-4 space-y-4">
              <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-100 uppercase tracking-wide">How to Cancel Your Internet Service</h4>

              <div className="overflow-x-auto border border-gray-200 dark:border-white/10 rounded-lg">
                <table className="w-full text-xs" style={{ minWidth: 580 }}>
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-800 text-left">
                      <th className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300 w-[90px]">Provider</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Phone</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Online</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Equipment Return</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="align-top">
                      <td className="px-3 py-2.5 align-middle"><img src="/images/vendors/cogeco.png" alt="Cogeco" className="h-6 object-contain" /></td>
                      <td className="px-3 py-2.5"><a href="tel:+18664283517" className="font-bold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline underline-offset-2">1-866-428-3517</a></td>
                      <td className="px-3 py-2.5"><a href="https://customer-services.cogeco.ca/" target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 font-medium underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200">customer-services.cogeco.ca</a></td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">Free courier pickup (Canada Post or Purolator). Do not return to a store.</td>
                    </tr>
                    <tr className="align-top bg-gray-50/50">
                      <td className="px-3 py-2.5 align-middle"><img src="/images/vendors/bell.png" alt="Bell" className="h-6 object-contain" /></td>
                      <td className="px-3 py-2.5"><a href="tel:3102355" className="font-bold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline underline-offset-2">310-BELL (2355)</a></td>
                      <td className="px-3 py-2.5"><a href="https://support.bell.ca" target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 font-medium underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200">support.bell.ca</a></td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">Prepaid shipping label mailed within 10 business days. Drop at any Purolator or Canada Post. Do not return to a Bell store.</td>
                    </tr>
                    <tr className="align-top">
                      <td className="px-3 py-2.5 align-middle"><img src="/images/vendors/rogers.png" alt="Rogers" className="h-6 object-contain" /></td>
                      <td className="px-3 py-2.5"><a href="tel:+18887643771" className="font-bold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline underline-offset-2">1-888-ROGERS1 (764-3771)</a></td>
                      <td className="px-3 py-2.5"><a href="https://www.rogers.com" target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 font-medium underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200">rogers.com</a></td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">Canada Post return label emailed within 24 hrs (check junk). Drop at any Canada Post outlet (e.g. Shoppers at Division St. or Princess St.).</td>
                    </tr>
                    <tr className="align-top bg-gray-50/50">
                      <td className="px-3 py-2.5 align-middle"><img src="/images/vendors/teksavvy.jpg" alt="TekSavvy" className="h-8 object-contain" /></td>
                      <td className="px-3 py-2.5"><a href="tel:+18777791575" className="font-bold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline underline-offset-2">1-877-779-1575</a></td>
                      <td className="px-3 py-2.5"><a href="https://www.teksavvy.com/support/" target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 font-medium underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200">teksavvy.com/support</a></td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">Return label provided if modem was rented. If you purchased your modem, it's yours to keep or sell.</td>
                    </tr>
                    <tr className="align-top">
                      <td className="px-3 py-2.5 align-middle"><img src="/images/vendors/start.ca.jpg" alt="Start.ca" className="h-6 object-contain" /></td>
                      <td className="px-3 py-2.5"><a href="tel:+18664345888" className="font-bold text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200 underline underline-offset-2">1-866-434-5888</a></td>
                      <td className="px-3 py-2.5"><a href="https://www.start.ca/support/live-chat" target="_blank" rel="noopener noreferrer" className="text-red-700 dark:text-red-400 font-medium underline underline-offset-2 hover:text-red-900 dark:hover:text-red-200">start.ca/support/live-chat</a></td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">Prepaid Purolator or Canada Post label emailed after notification.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

            {/* Task 1: Confirm service cancellation CTA */}
            {internetDisconnectedTask && (() => {
              const idt = internetDisconnectedTask;
              const idDone = internetDisconnectedChecked && !!idt.attachment;
              return (
              <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                idDone ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${idDone ? 'bg-green-600' : 'bg-red-600'}`}>
                    {idDone ? <Check size={14} className="text-white" strokeWidth={3} /> : <Upload size={14} className="text-white" />}
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${idDone ? 'text-green-600' : 'text-red-500'}`}>Task {idt.sortOrder + 1}</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${idDone ? 'text-green-900' : 'text-red-900'}`}>Confirm Service Cancellation</h4>
                  </div>
                </div>

                <div
                  role="checkbox"
                  aria-checked={internetDisconnectedChecked}
                  tabIndex={0}
                  onClick={() => handleToggleDualTask('internet-disconnected')}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleToggleDualTask('internet-disconnected'); } }}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    internetDisconnectedChecked ? 'bg-green-600 border-green-600 text-white' : 'border-red-400 group-hover:border-red-500'
                  }`}>
                    {internetDisconnectedChecked && <Check size={13} strokeWidth={3} />}
                  </div>
                  <span className={`text-sm leading-snug ${idDone ? 'text-green-800 line-through' : 'text-gray-800'}`}>{idt.label}</span>
                </div>
                {internetDisconnectedChecked && !idt.attachment && (
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium ml-8">Upload your confirmation below to complete this step.</p>
                )}
                {!internetDisconnectedChecked && idt.attachment && (
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium ml-8">Check the box above to complete this step.</p>
                )}

                <div className={`rounded-lg p-4 ${idt.attachment ? 'bg-white border border-green-200' : 'bg-white border-2 border-dashed border-red-300'}`}>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Upload a screenshot of your cancellation confirmation as proof.</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-3">Accepted formats: JPG, PNG, WEBP, HEIC, PDF</p>

                  {idt.attachment ? (
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-md px-3 py-2.5">
                      <Paperclip size={14} className="text-green-600 shrink-0" />
                      <span className="text-sm text-green-900 font-medium truncate flex-1">{idt.attachment.filename}</span>
                      <a href={resolveMediaUrl(idt.attachment.url) ?? '#'} target="_blank" rel="noopener noreferrer" className="text-xs text-green-700 dark:text-green-400 hover:text-green-900 font-medium shrink-0">View</a>
                      <span className="text-green-300 shrink-0">|</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(idt)}
                        disabled={deletingAttachmentId === idt.dbId}
                        className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-700 dark:hover:text-red-400 font-medium shrink-0 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        {deletingAttachmentId === idt.dbId ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={internetFileInputRef}
                        type="file"
                        accept="image/*,.pdf,.heic,.heif,image/heic,image/heif"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
                            const dateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                            const name = tenant ? `${tenant.firstName} ${tenant.lastName}` : 'Tenant';
                            const renamedFile = new File([file], `${dateStr} - ${name} - Internet Cancellation Confirmation${ext}`, { type: file.type });
                            handleUploadAttachment(idt, renamedFile);
                          }
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => internetFileInputRef.current?.click()}
                        disabled={uploadingItemId != null && uploadingItemId === idt.dbId}
                        className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {(uploadingItemId != null && uploadingItemId === idt.dbId) ? (<><Loader2 size={14} className="animate-spin" /> Uploading…</>) : (<><Upload size={14} /> Upload Confirmation</>)}
                      </button>
                    </>
                  )}
                </div>
              </div>
              );
            })()}

            {/* Equipment identification guide */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2.5">
                <Package size={18} className="text-red-700 dark:text-red-400 shrink-0" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">What Does My Router / Wi-Fi Equipment Look Like?</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Your internet provider will ask you to return the router or Wi-Fi access point they supplied when your service was installed. Below are examples of common equipment from each provider — yours may look slightly different depending on your plan, but the general shape and size will be similar.
              </p>
              <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 flex items-center gap-2.5">
                <AlertCircle size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />
                <p className="text-xs text-gray-700 dark:text-gray-300">Providers charge <span className="font-semibold">$100 – $250+</span> for unreturned equipment — return your modem/router promptly and keep your tracking number.</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-3 w-full aspect-square flex items-center justify-center">
                    <img src="/images/vendors/cogeco_equipment.png" alt="Cogeco router / Wi-Fi gateway" className="max-h-full max-w-full object-contain" />
                  </div>
                  <img src="/images/vendors/cogeco.png" alt="Cogeco" className="h-4 object-contain" />
                </div>
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-3 w-full aspect-square flex items-center justify-center">
                    <img src="/images/vendors/bell_equipment.png" alt="Bell Home Hub router and Wi-Fi pods" className="max-h-full max-w-full object-contain" />
                  </div>
                  <img src="/images/vendors/bell.png" alt="Bell" className="h-4 object-contain" />
                </div>
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-3 w-full aspect-square flex items-center justify-center">
                    <img src="/images/vendors/rogers_equipment.png" alt="Rogers Ignite Wi-Fi gateway" className="max-h-full max-w-full object-contain" />
                  </div>
                  <img src="/images/vendors/rogers.png" alt="Rogers" className="h-4 object-contain" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                Look for a device like these near your TV or where your internet cable enters the unit. Don't forget to also return any additional Wi-Fi pods or extenders that may have been placed in other rooms.
              </p>
            </div>

            {/* Task 2: Equipment return CTA */}
            {internetRouterTask && (
              <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                internetRouterTask.completed
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    internetRouterTask.completed ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {internetRouterTask.completed
                      ? <Check size={14} className="text-white" strokeWidth={3} />
                      : <Package size={14} className="text-white" />
                    }
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${
                      internetRouterTask.completed ? 'text-green-600' : 'text-red-500'
                    }`}>Task {internetRouterTask.sortOrder + 1}</p>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${
                      internetRouterTask.completed ? 'text-green-900' : 'text-red-900'
                    }`}>Equipment Return</h4>
                  </div>
                </div>

                <div
                  role="checkbox"
                  aria-checked={internetRouterTask.completed}
                  tabIndex={0}
                  onClick={() => handleToggleSimpleTask('internet-router-returned')}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleToggleSimpleTask('internet-router-returned'); } }}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    internetRouterTask.completed
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-red-400 group-hover:border-red-500'
                  }`}>
                    {internetRouterTask.completed && <Check size={13} strokeWidth={3} />}
                  </div>
                  <span className={`text-sm leading-snug ${internetRouterTask.completed ? 'text-green-800 line-through' : 'text-gray-800'}`}>
                    {internetRouterTask.label}
                  </span>
                </div>
              </div>
            )}
          </div>
          </CollapsibleSection>
        </section>

        {/* Section: Settle Up Final Balances */}
        {moveOut && (
          <section id="settle-up">
            <CollapsibleSection number={6} title="Settle Up Final Balances" isComplete={false} defaultOpen={true} forceToggle={sectionToggle} sectionId="balances" forceOpenId={forceOpenId}>

            {/* Balance summary */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-4">
              <div className="flex items-center gap-2.5">
                <DollarSign size={20} className="text-red-700 dark:text-red-400 shrink-0" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Account Balance</h3>
              </div>

              {ledgerLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-red-700 dark:text-red-400" />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading balance...</span>
                </div>
              ) : ledger ? (
                <>
                  {ledger.netBalance > 0 ? (
                    <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/40 p-5 flex items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
                        <DollarSign size={20} className="text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-800">
                          You have an outstanding balance of ${ledger.netBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Please settle your remaining balance before your move-out date.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-950/30 p-5 flex items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
                        <CheckCircle2 size={20} className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-green-800 dark:text-green-300">You have no outstanding balance</p>
                        <p className="text-xs text-green-600 mt-0.5">Your account is fully settled.</p>
                      </div>
                    </div>
                  )}

                  {ledger.transactions.length > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Payment History</h4>
                        <button
                          type="button"
                          onClick={handleDownloadRentReceipt}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <FileDown size={13} />
                          Download Rent Receipt
                        </button>
                      </div>
                      <div className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                              <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Date</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Type</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide hidden sm:table-cell">Memo</th>
                                <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Amount</th>
                                <th className="text-right px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Balance</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {transactionsWithBalance.map((tx) => {
                                const isCredit = CREDIT_TYPES.has(tx.type);
                                const balPositive = tx.runningBalance > 0;
                                const balNegative = tx.runningBalance < 0;
                                return (
                                  <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-white/5">
                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{tx.date}</td>
                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{tx.type}</td>
                                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 truncate max-w-[150px] hidden sm:table-cell">{tx.memo || '—'}</td>
                                    <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${isCredit ? 'text-green-700' : 'text-gray-900'}`}>
                                      {isCredit ? '-' : ''}${Math.abs(tx.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${balPositive ? 'text-red-700' : balNegative ? 'text-green-700' : 'text-gray-900'}`}>
                                      ${Math.abs(tx.runningBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                      {balNegative ? ' CR' : ''}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="bg-gray-50 dark:bg-white/5 border border-dashed border-gray-300 dark:border-white/15 rounded-lg p-6 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Account balance information will be available once your move-out is processed.</p>
                </div>
              )}
            </div>
            </CollapsibleSection>
          </section>
        )}

        {/* Section: Selling Items to Incoming Tenants */}
        <section id="furniture-sale">
          <CollapsibleSection number={7} title="Selling Items to Incoming Tenants [Optional]" isComplete={furnitureSaleSubmitted && furnitureTaskComplete} forceToggle={sectionToggle} sectionId="furniture-sale" forceOpenId={forceOpenId}>

          {/* Step 4.1 — Indicate Interest */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <Users size={20} className="text-red-700 dark:text-red-400 shrink-0" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Step 4.1 — Are You Interested in Selling Furniture?
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Are you interested in selling furniture to the incoming group of tenants? Amber Peak can help facilitate this interaction, but does not guarantee that you will be successful.
            </p>

            {furnitureSaleSubmitted ? (
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle2 size={20} className="text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">Consent Registered</p>
                  <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                    Your consent has been recorded. Our team has been notified and will take it from here.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      checked={furnitureSaleSellerConsent}
                      onChange={(e) => setFurnitureSaleSellerConsent(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 rounded border-2 border-gray-300 dark:border-white/15 peer-checked:border-red-700 peer-checked:bg-red-700 transition-colors flex items-center justify-center">
                      {furnitureSaleSellerConsent && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 transition-colors">
                    I am interested in selling furniture or personal items to the incoming tenant(s). I consent to Amber Peak sharing my contact information with the incoming tenant(s). Amber Peak will notify them of my interest — if they wish to purchase, they will contact me directly.
                  </span>
                </label>

                {furnitureSaleSellerConsent && (
                  <div className="pt-2">
                    <button
                      onClick={async () => {
                        if (!moveOut?.id) return;
                        setFurnitureSaleSubmitting(true);
                        try {
                          const result = await registerFurnitureSaleSellerConsent(
                            getToken,
                            moveOut.id,
                            furnitureSaleSellerConsent
                          );
                          setFurnitureSaleConsentResult(result);
                          setFurnitureSaleSubmitted(true);
                        } catch (err) {
                          console.error('Failed to register furniture sale consent', err);
                        } finally {
                          setFurnitureSaleSubmitting(false);
                        }
                      }}
                      disabled={furnitureSaleSubmitting}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-700 hover:bg-red-800 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {furnitureSaleSubmitting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          <Users size={14} />
                          Submit Consent
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 4.2 — Documenting & Preparing Items for Sale */}
          <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4 transition-opacity ${!furnitureSaleSubmitted ? 'opacity-40 pointer-events-none select-none' : ''}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div className="flex items-start gap-2.5">
                <Tag size={20} className={`shrink-0 mt-0.5 ${furnitureSaleSubmitted ? 'text-red-700' : 'text-gray-400'}`} />
                <h3 className={`text-base font-semibold ${furnitureSaleSubmitted ? 'text-gray-900' : 'text-gray-400'}`}>
                  Step 4.2 — Documenting & Preparing Items for Sale
                </h3>
              </div>
              {saleItems.length > 0 && (
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-7 sm:ml-0 shrink-0">{saleItems.length} item{saleItems.length !== 1 ? 's' : ''} uploaded</span>
              )}
            </div>
            {!furnitureSaleSubmitted && (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">Complete Step 4.1 above to unlock this section.</p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Great — you and the incoming tenant(s) have agreed to a sale of furniture or personal items.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Follow the steps below to complete a seamless transition of these possessions.
            </p>

            {/* Numbered instructions */}
            <div className="space-y-3 ml-1">
              <div className="flex gap-2.5">
                <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0 mt-[1px]">1</div>
                <div className="text-sm text-gray-700 dark:text-gray-300 leading-5"><span className="font-semibold">Agree which items are being sold with the purchaser.</span></div>
              </div>
              <div className="flex gap-2.5">
                <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0 mt-[1px]">2</div>
                <div className="text-sm text-gray-700 dark:text-gray-300 leading-5"><span className="font-semibold">Photograph each item being sold.</span> Photograph every piece of furniture or personal item that will remain in the unit so there is a clear visual record of what was agreed upon.</div>
              </div>
              <div className="flex gap-2.5">
                <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0 mt-[1px]">3</div>
                <div className="text-sm text-gray-700 dark:text-gray-300 leading-5"><span className="font-semibold">Upload and label all items.</span></div>
              </div>
              <div className="flex gap-2.5">
                <div className="w-5 h-5 rounded-full bg-red-700 text-white text-[11px] font-bold leading-[20px] text-center shrink-0 mt-[1px]">4</div>
                <div className="text-sm text-gray-700 dark:text-gray-300 leading-5"><span className="font-semibold">Mark each item with a post-it note and neatly stage all sold items together in each room.</span></div>
              </div>
            </div>

            {/* Warning callout */}
            <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-3">
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                🚨 Possessions that remain in the unit that do not meet these requirements of transfer will be <span className="font-semibold">considered garbage and removed at your expense</span> at the time of your move-out appointment.
              </p>
            </div>

            {/* Instructional video clips */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { step: 1, title: 'Form an Agreement with Your Buyer', section: 'furniture-sale-clip-1', desc: 'Outgoing and incoming tenants shaking hands' },
                { step: 2, title: 'Photograph Items', section: 'furniture-sale-clip-2', desc: 'Taking photos of furniture items in your unit' },
                { step: 3, title: 'Upload Photos', section: 'furniture-sale-clip-3', desc: 'Uploading furniture photos into this interface' },
                { step: 4, title: 'Label Items & Neatly Stage all Sold Items Together', section: 'furniture-sale-clip-4', desc: 'Push furniture into a corner and place a sticky note on each piece' },
              ].map((clip) => {
                const items = getContentForSection(clip.section);
                if (items.length > 0) {
                  return (
                    <div key={clip.step} className="relative aspect-[9/16] rounded-lg border border-gray-200 dark:border-white/10 overflow-hidden bg-black">
                      <InlineVideo src={items[0].cloudfrontUrl} className="w-full h-full object-cover" />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-black/70 backdrop-blur-sm px-2.5 flex flex-col justify-center">
                        <span className="block text-[10px] font-bold text-white/70 uppercase tracking-wider">Step {clip.step}</span>
                        <span className="block text-xs font-semibold text-white leading-tight mt-0.5">{clip.title}</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={clip.step} className="relative aspect-[9/16] bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-white/10 overflow-hidden flex flex-col items-center justify-center px-2 text-center">
                    <Play size={20} className="text-gray-300 mb-1.5" />
                    <span className="text-[9px] text-gray-400 dark:text-gray-500 leading-tight">{clip.desc}</span>
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-black/70 backdrop-blur-sm px-2.5 flex flex-col justify-center">
                      <span className="block text-[10px] font-bold text-white/70 uppercase tracking-wider">Step {clip.step}</span>
                      <span className="block text-xs font-semibold text-white leading-tight mt-0.5">{clip.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Step 1 — Upload Your Purchase Confirmation */}
            {furnitureSaleSubmitted && (() => {
              const hasProof = !!(furnitureProof || moveOut?.furnitureSaleProofFilename);
              return (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Step 1</p>
              <div className={`rounded-xl p-5 space-y-3 transition-colors ${
                hasProof
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    hasProof ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {hasProof
                      ? <Check size={14} className="text-white" strokeWidth={3} />
                      : <FileText size={14} className="text-white" />
                    }
                  </div>
                  <div>
                    <h4 className={`text-sm font-bold uppercase tracking-wide ${
                      hasProof ? 'text-green-900' : 'text-red-900'
                    }`}>Upload Your Purchase Confirmation</h4>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  If you have sold furniture to the incoming tenants, please upload a screenshot or PDF showing proof of the agreement between both parties (e.g. an email confirmation, e-transfer receipt, or written agreement).
                </p>

                {(furnitureProof || moveOut?.furnitureSaleProofFilename) ? (
                  <div className="flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3">
                    {(furnitureProof?.mimeType || moveOut?.furnitureSaleProofMimeType || '')?.startsWith('image/') ? (
                      furnitureProof?.downloadUrl ? (
                        <img
                          src={furnitureProof.downloadUrl}
                          alt="Proof of purchase"
                          className="w-10 h-10 rounded object-cover border border-gray-200 dark:border-white/10 shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center shrink-0">
                          <ImageIcon size={18} className="text-gray-400 dark:text-gray-500" />
                        </div>
                      )
                    ) : (
                      <div className="w-10 h-10 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 flex items-center justify-center shrink-0">
                        <FileText size={18} className="text-red-600 dark:text-red-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{furnitureProof?.filename || moveOut?.furnitureSaleProofFilename}</p>
                      {(furnitureProof?.size ?? moveOut?.furnitureSaleProofSize) != null && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">{((furnitureProof?.size ?? moveOut?.furnitureSaleProofSize ?? 0) / 1024).toFixed(0)} KB</p>
                      )}
                    </div>
                    {furnitureProof?.downloadUrl ? (
                      <a
                        href={furnitureProof.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-400 font-medium shrink-0"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">Loading...</span>
                    )}
                    <button
                      onClick={handleProofDelete}
                      disabled={furnitureProofDeleting}
                      className="text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors shrink-0"
                    >
                      {furnitureProofDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={furnitureProofInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleProofUpload(file);
                        e.target.value = '';
                      }}
                    />
                    <div
                      onClick={() => furnitureProofInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleProofUpload(file);
                      }}
                      className="border-2 border-dashed border-gray-300 dark:border-white/15 hover:border-red-400 rounded-lg px-4 py-6 text-center cursor-pointer transition-colors"
                    >
                      {furnitureProofUploading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 size={18} className="animate-spin text-red-600 dark:text-red-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <Upload size={22} className="mx-auto text-gray-400 dark:text-gray-500 mb-1.5" />
                          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Drop file here or click to upload</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">JPEG, PNG, GIF, WebP, or PDF — max 10 MB</p>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
              );
            })()}

            {/* Steps 2 & 3 — Photograph & Upload Your Items */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Steps 2 & 3</p>
              <div className="rounded-xl p-5 space-y-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-red-600">
                    <Tag size={14} className="text-white" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900 dark:text-white">Photograph & Upload Your Items</h4>
                    {saleItems.length > 0 && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{saleItems.length} item{saleItems.length !== 1 ? 's' : ''} uploaded</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  Take a photo of each piece of furniture or personal item that will remain in the unit, then upload and tag each item below.
                </p>

                {/* Item gallery */}
                {saleItemsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-gray-400 dark:text-gray-500" />
                  </div>
                ) : saleItems.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {saleItems.map((item, idx) => (
                      <div
                        key={item.id}
                        className="group relative bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 overflow-hidden cursor-pointer"
                        onClick={() => openEditItem(item)}
                      >
                        <div className="relative">
                          {item.imageUrl ? (
                            <img
                              src={resolveMediaUrl(item.imageUrl) ?? undefined}
                              alt={item.name}
                              className="w-full aspect-square object-cover"
                            />
                          ) : (
                            <div className="w-full aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                              <ImageIcon size={20} className="text-gray-300" />
                            </div>
                          )}
                          <span className="absolute top-1 left-1 min-w-[1.25rem] h-5 rounded-full bg-red-700 text-white text-[10px] font-bold flex items-center justify-center px-1">{idx + 1}</span>
                        </div>
                        <div className="px-1.5 py-1">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                          {item.description && (
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{item.description}</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSaleItem(item.id); }}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Drop zone for multi-photo upload */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files.length > 0) handleFilesSelected(e.dataTransfer.files);
                  }}
                  onClick={() => dropInputRef.current?.click()}
                  className={`flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    dragOver
                      ? 'border-red-400 bg-red-50'
                      : 'border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50/30'
                  }`}
                >
                  <input
                    ref={dropInputRef}
                    type="file"
                    multiple
                    accept="image/*,.heic,.heif"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) handleFilesSelected(e.target.files);
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  <Upload size={24} className={dragOver ? 'text-red-400 mb-2' : 'text-gray-300 mb-2'} />
                  <p className={`text-sm font-medium ${dragOver ? 'text-red-600' : 'text-gray-500'}`}>
                    {dragOver ? 'Drop photos here' : 'Drag photos here or click to browse'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">JPG, PNG, WebP, or HEIC</p>
                </div>
              </div>
            </div>

            {/* Step 4 — Confirm All Furniture Activities Complete */}
            {furnitureConfirmedTask && (() => {
              const proofDone = !!(furnitureProof || moveOut?.furnitureSaleProofFilename);
              const photosDone = saleItems.length > 0;
              const canConfirm = proofDone && photosDone;
              const isComplete = furnitureConfirmedTask.completed && canConfirm;
              const locked = !canConfirm;

              return (
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Step 4</p>
                <div className={`rounded-xl p-5 space-y-4 transition-colors ${
                  isComplete
                    ? 'bg-green-50 border border-green-200'
                    : locked
                      ? 'bg-gray-50 border border-gray-200'
                      : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      isComplete ? 'bg-green-600' : locked ? 'bg-gray-400' : 'bg-red-600'
                    }`}>
                      {isComplete
                        ? <Check size={14} className="text-white" strokeWidth={3} />
                        : <PackageOpen size={14} className="text-white" />
                      }
                    </div>
                    <div>
                      <h4 className={`text-sm font-bold uppercase tracking-wide ${
                        isComplete ? 'text-green-900' : locked ? 'text-gray-500' : 'text-red-900'
                      }`}>Confirm All Furniture Sale Activities</h4>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    {isComplete
                      ? 'Thank you for confirming. If anything changes, you can update this at any time.'
                      : locked
                        ? `Complete the following steps to unlock this confirmation:`
                        : 'By checking the box below, you confirm that you have:'}
                  </p>

                  {locked ? (
                    <ul className="space-y-1.5 ml-1">
                      <li className={`flex items-start gap-2 text-xs ${proofDone ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${proofDone ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {proofDone ? 'Purchase confirmation uploaded' : 'Upload your purchase confirmation (Step 1)'}
                      </li>
                      <li className={`flex items-start gap-2 text-xs ${photosDone ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${photosDone ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {photosDone ? 'Furniture photographs uploaded' : 'Photograph and upload at least one item (Steps 2 & 3)'}
                      </li>
                    </ul>
                  ) : (
                    <ul className="space-y-1.5 ml-1">
                      <li className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                        Uploaded your purchase confirmation
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                        Photographed, tagged, and uploaded all items being sold
                      </li>
                      <li className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                        Neatly piled all sold items together in each room with post-it notes
                      </li>
                    </ul>
                  )}

                  <div
                    role="checkbox"
                    aria-checked={isComplete}
                    aria-disabled={locked}
                    tabIndex={locked ? -1 : 0}
                    onClick={() => { if (!locked) handleToggleSimpleTask('furniture-items-confirmed'); }}
                    onKeyDown={(e) => { if (!locked && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); handleToggleSimpleTask('furniture-items-confirmed'); } }}
                    className={`flex items-center gap-3 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isComplete
                        ? 'bg-green-600 border-green-600 text-white'
                        : locked
                          ? 'border-gray-300'
                          : 'border-red-400 group-hover:border-red-500'
                    }`}>
                      {isComplete && <Check size={13} strokeWidth={3} />}
                    </div>
                    <span className={`text-sm leading-snug ${isComplete ? 'text-green-800 line-through' : locked ? 'text-gray-400' : 'text-gray-800'}`}>
                      I confirm that all furniture sale activities have been completed
                    </span>
                  </div>
                </div>
              </div>
              );
            })()}


            {/* HEIC conversion overlay */}
            {heicConverting && (
              <div className="fixed inset-0 xl:left-72 z-[9999] bg-black/70 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">
                  <Loader2 size={32} className="animate-spin text-red-600 dark:text-red-400 mx-auto mb-4" />
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Converting photos...</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">HEIC images are being converted to JPEG</p>
                </div>
              </div>
            )}

            {/* Tagging wizard overlay */}
            {pendingFiles.length > 0 && pendingFiles[taggingIndex] && !heicConverting && (
              <div className="fixed inset-0 xl:left-72 z-[9999] bg-black/70 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/5">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Tag Your Item</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Photo {taggingIndex + 1} of {pendingFiles.length}
                      </p>
                    </div>
                    <button
                      onClick={handleTaggingCancelAll}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      Cancel All
                    </button>
                  </div>

                  {/* Photo preview */}
                  <div className="px-5 pt-4">
                    <img
                      src={taggingPreviewUrl ?? undefined}
                      alt={`Preview ${taggingIndex + 1}`}
                      className="w-full max-h-[50vh] object-contain rounded-xl bg-gray-100 dark:bg-gray-800"
                    />
                  </div>

                  {/* Inputs */}
                  <div className="px-5 pt-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Item Name *</label>
                      <input
                        type="text"
                        placeholder="e.g. Couch, Dining Table, Desk Lamp"
                        value={taggingName}
                        onChange={(e) => setTaggingName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
                      <textarea
                        placeholder="Condition, dimensions, color, etc."
                        value={taggingDescription}
                        onChange={(e) => setTaggingDescription(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="px-5 pt-3">
                    <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-600 rounded-full transition-all duration-300"
                        style={{ width: `${((taggingIndex + 1) / pendingFiles.length) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="px-5 py-4 flex gap-2">
                    <button
                      onClick={handleTaggingSkip}
                      disabled={taggingUploading}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleTaggingSave}
                      disabled={!taggingName.trim() || taggingUploading}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-700 rounded-lg hover:bg-red-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      {taggingUploading ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Uploading…
                        </>
                      ) : taggingIndex < pendingFiles.length - 1 ? (
                        'Save & Next'
                      ) : (
                        'Save & Finish'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit item modal */}
            {editingItem && (
              <div
                className="fixed inset-0 xl:left-72 z-[9999] bg-black/70 flex items-center justify-center p-4"
                onClick={handleEditCancel}
              >
                <div
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/5">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Edit Item</p>
                    <button
                      onClick={handleEditCancel}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {/* Photo preview */}
                  <div className="px-5 pt-4">
                    {editingItem.imageUrl ? (
                      <img
                        src={resolveMediaUrl(editingItem.imageUrl) ?? undefined}
                        alt={editingItem.name}
                        className="w-full max-h-[40vh] object-contain rounded-xl bg-gray-100 dark:bg-gray-800"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center">
                        <ImageIcon size={32} className="text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Inputs */}
                  <div className="px-5 pt-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Item Name *</label>
                      <input
                        type="text"
                        placeholder="e.g. Couch, Dining Table, Desk Lamp"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
                      <textarea
                        placeholder="Condition, dimensions, color, etc."
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="px-5 py-4 flex gap-2">
                    <button
                      onClick={handleEditCancel}
                      disabled={editSaving}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditSave}
                      disabled={!editName.trim() || editSaving}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-700 rounded-lg hover:bg-red-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      {editSaving ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Saving…
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          </CollapsibleSection>
        </section>

        {/* Section: Reference Letter */}
        <section id="reference-letter">
          <CollapsibleSection number={8} title="Reference Letter [Optional]" isComplete={referenceLetterRequested} forceToggle={sectionToggle} sectionId="reference-letter" forceOpenId={forceOpenId}>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <FileText size={20} className="text-red-700 dark:text-red-400 shrink-0" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Tenant Reference Letter</h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              We love helping our fantastic tenants find their next home. A reference letter from your landlord can be an important part of forming your application for new properties.
            </p>

            {!referenceLetterRequested ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">How your reference letter is evaluated:</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">Your reference letter will contain and speak to the following criteria, as laid out in the rubric below.</p>
                  <div className="overflow-x-auto -mx-5 px-5 pb-2 md:mx-0 md:px-0">
                    <div className="min-w-[640px]">
                      <div className="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr] text-xs border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                        {/* Header row */}
                        <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-white/10">Criteria</div>
                        <div className="bg-green-50 dark:bg-green-950/30 px-3 py-2.5 font-semibold text-green-800 dark:text-green-300 border-b border-l border-gray-200 dark:border-white/10 text-center">Exceeds Expectations</div>
                        <div className="bg-gray-50 dark:bg-white/5 px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-l border-gray-200 dark:border-white/10 text-center">Meets Expectations</div>
                        <div className="bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 font-semibold text-amber-800 dark:text-amber-300 border-b border-l border-gray-200 dark:border-white/10 text-center">Below Expectations</div>
                        <div className="bg-red-50 dark:bg-red-950/40 px-3 py-2.5 font-semibold text-red-800 border-b border-l border-gray-200 dark:border-white/10 text-center">Significantly Below</div>

                        {[
                          { label: 'Rent & Financial Obligations', exceeds: 'Never late on payments, often paid early', meets: 'Always paid rent by the due date', below: 'Frequently late on payments', sigBelow: 'Consistently late on payments' },
                          { label: 'Lease Compliance', exceeds: 'Fully compliant throughout the entire tenancy: respectful of all rules, neighbours, and shared spaces. A model tenant.', meets: 'Generally compliant with the lease terms, with only minor issues resolved quickly.', below: 'Multiple issues requiring follow-up, such as improper garbage disposal, noise complaints, or unauthorized guests.', sigBelow: 'Serious or repeated lease violations requiring formal intervention.' },
                          { label: 'Maintenance Reporting', exceeds: 'Reported promptly with detailed, helpful descriptions', meets: 'Reported issues within a reasonable timeframe', below: 'Delayed reporting that led to worsened conditions', sigBelow: 'Failed to report known issues causing damage' },
                          { label: 'Notice of Move-Out', exceeds: 'Notice given well in advance of requirement', meets: 'Proper notice as required by the lease', below: 'Late or incomplete notice given', sigBelow: 'No notice provided or unit abandoned' },
                          { label: 'Unit Condition During Tenancy', exceeds: 'Always in fantastic condition: sanitary, tidy, and in excellent condition throughout the entire tenancy. Treats the property like an owner.', meets: 'Nearly always well-kept: clean, sanitary, and tidy throughout the tenancy.', below: 'Noticeably lacking in upkeep: cleanliness issues, clutter, or minor preventable damage.', sigBelow: 'Persistently neglected: unsanitary conditions, significant damage, or deterioration of the property.' },
                          { label: 'Communication', exceeds: 'Always responsive, professional, and courteous', meets: 'Responsive within a reasonable timeframe', below: 'Often unresponsive or difficult to reach', sigBelow: 'Unreachable or hostile communications' },
                          { label: 'Move-Out Condition', exceeds: 'Fully vacated and thoroughly cleaned: no items left behind, no damage, move-in ready.', meets: 'Vacated and reasonably clean: normal wear only, minimal touch-up needed.', below: 'Personal items, garbage, or furniture left behind: significant additional cleaning required beyond normal standards.', sigBelow: 'Substantial items, debris, or damage left behind: unit required major cleaning, repairs, or disposal before it could be re-rented.' },
                          { label: 'Would Rent Again', exceeds: 'Enthusiastically, without reservation', meets: 'Yes, under similar circumstances', below: 'With reservations', sigBelow: 'Would not rent again' },
                        ].map((row, i) => {
                          const isLast = i === 7;
                          const borderB = isLast ? '' : 'border-b border-gray-200';
                          return (
                            <React.Fragment key={i}>
                              <div className={`bg-gray-50 px-3 py-2.5 font-medium text-gray-700 ${borderB}`}>{row.label}</div>
                              <div className={`px-3 py-2.5 text-gray-600 border-l border-gray-200 ${borderB}`}>{row.exceeds}</div>
                              <div className={`px-3 py-2.5 text-gray-600 border-l border-gray-200 ${borderB}`}>{row.meets}</div>
                              <div className={`px-3 py-2.5 text-gray-600 border-l border-gray-200 ${borderB}`}>{row.below}</div>
                              <div className={`px-3 py-2.5 text-gray-600 border-l border-gray-200 ${borderB}`}>{row.sigBelow}</div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 md:hidden">Scroll sideways to see all columns</p>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={async () => {
                      if (!moveOut?.id) return;
                      setReferenceLetterRequesting(true);
                      try {
                        await requestReferenceLetter(getToken, moveOut.id);
                        setReferenceLetterRequested(true);
                      } catch (err) {
                        console.error('Failed to request reference letter', err);
                        alert('Something went wrong submitting your request. Please try again.');
                      } finally {
                        setReferenceLetterRequesting(false);
                      }
                    }}
                    disabled={referenceLetterRequesting || !moveOut?.id}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {referenceLetterRequesting ? (
                      <><Loader2 size={16} className="animate-spin" /> Requesting...</>
                    ) : (
                      <><FileText size={16} /> Request a Reference Letter</>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">Reference letter requested!</p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Our team has received your request. We&apos;ll prepare your reference letter and have it ready for you around your move-out date. If you have any questions, feel free to reach out.
                  </p>
                </div>
              </div>
            )}
          </div>
          </CollapsibleSection>
        </section>

        {/* ── Section 9: Feedback ── */}
        {moveOut && (
          <section id="feedback">
            <CollapsibleSection number={9} title="Share Your Feedback on the Move-Out Portal [Optional]" isComplete={feedbackSubmitted} forceToggle={sectionToggle} sectionId="feedback" forceOpenId={forceOpenId}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg flex-shrink-0">
                    <MessageSquare size={20} className="text-amber-700 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Do You Have Feedback?</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      How did you like your move-out portal experience? Were there issues? Areas for improvement? Do you have ideas for improvement? Please share your thoughts here.
                    </p>
                  </div>
                </div>

                {feedbackSubmitted ? (
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-lg p-4 flex items-center gap-3">
                    <Check size={18} className="text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800 dark:text-green-300">Thank you for your feedback! We really appreciate it.</p>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Share your thoughts, suggestions, or anything else you'd like us to know..."
                      className="w-full border border-gray-300 dark:border-white/15 rounded-lg p-3 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-red-600 focus:border-red-600 outline-none resize-y min-h-[120px]"
                      rows={5}
                      disabled={feedbackSubmitting}
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={async () => {
                          if (!feedbackText.trim() || !moveOut) return;
                          setFeedbackSubmitting(true);
                          try {
                            await submitMoveOutFeedback(getToken, moveOut.id, feedbackText.trim());
                            setFeedbackSubmitted(true);
                          } catch (err) {
                            console.error('Failed to submit feedback:', err);
                          } finally {
                            setFeedbackSubmitting(false);
                          }
                        }}
                        disabled={!feedbackText.trim() || feedbackSubmitting}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-700 hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {feedbackSubmitting ? (
                          <><Loader2 size={14} className="animate-spin" /> Sending...</>
                        ) : (
                          <><Send size={14} /> Submit Feedback</>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </CollapsibleSection>
          </section>
        )}

        {/* ── Section 10: Google Review (only shown if enabled on move-out record) ── */}
        {moveOut && moveOut.askForGoogleReview && (
          <section id="google-review">
            <CollapsibleSection number={10} title="Leave Us a Google Review [Optional]" isComplete={false} defaultOpen={true} forceToggle={sectionToggle} sectionId="google-review" forceOpenId={forceOpenId}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-5 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg flex-shrink-0">
                    <Star size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Enjoyed Your Experience With Us?</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      If you had a great time living with Amber Peak, we'd love for you to share that with the world. Strong, thoughtful reviews help us find other amazing tenants just like you — people who make our community what it is.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50/60 rounded-lg border border-amber-100 p-4 space-y-3">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Not sure what to write? Here are some ideas to get you started:</p>
                  <ul className="space-y-2.5 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9733;</span>
                      <span>What role did your Amber Peak home play in the friendships you built during school?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9733;</span>
                      <span>How did having a great place to live shape your overall university experience?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9733;</span>
                      <span>What's a favourite memory from your time in your unit — a dinner party, a study session, a late night with roommates?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9733;</span>
                      <span>Did living here make it easier to focus on school, stay involved on campus, or feel at home in a new city?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9733;</span>
                      <span>What would you tell a friend who's looking for a place to rent near campus?</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center pt-1">
                  <a
                    href="https://g.page/r/CZDhhrrPnJ9lEBM/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-white/5 text-gray-800 dark:text-gray-100 text-sm font-semibold rounded-lg transition-colors shadow-sm border border-gray-300 dark:border-white/15"
                  >
                    <img src="/google-g-icon.png" alt="" width={20} height={20} className="flex-shrink-0" />
                    Review Us on Google
                    <ExternalLink size={14} className="text-gray-400 dark:text-gray-500" />
                  </a>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Opens in a new tab — takes less than a minute</p>
                </div>
              </div>
            </CollapsibleSection>
          </section>
        )}


      </div>
    </>
  );
}
